/**
 * FeedHome - Main Landing Page (Claude/ChatGPT Style)
 * 
 * This IS the app. The home page is the creation flow.
 * - Big central input for natural language
 * - Full lead profile cards with all actions
 * - Detail panel for working through leads
 * 
 * Philosophy: Open app → Start working immediately
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Briefcase,
  UserCheck,
  Crown,
  Target,
  Building2,
  Zap,
  Globe,
  TrendingUp,
  Loader2,
  ArrowRight,
  Plus,
  Users,
  Handshake,
  MapPin,
  Home,
  ChevronDown,
  ChevronUp,
  Mail,
  Phone,
  Linkedin,
  ExternalLink,
  Copy,
  CheckCircle,
  Star,
  X,
  Check,
  MessageSquare,
  Send,
  Clock,
  ChevronRight,
  Trash2,
  RefreshCw,
  BookmarkPlus,
  MoreVertical,
  Sliders,
  ToggleLeft,
  ToggleRight,
  FileText,
  DollarSign,
  GraduationCap,
  Newspaper,
  Link2,
  Search,
  Filter,
  GripVertical,
  LayoutList,
  Columns,
  PlusCircle,
  Wand2,
  ArrowUpDown,
  CircleDot,
  CheckCircle2,
  XCircle,
  AlertCircle,
  ChevronLeft,
  Upload,
  Download,
  Share2,
  Copy as CopyIcon,
  Database,
  Cpu,
  History,
  MousePointer,
  Layers,
  Pencil,
  Save,
  PlusSquare,
  MinusCircle,
  StickyNote,
  UserCircle,
  User,
  Shield,
  Pill,
  FlaskConical
} from 'lucide-react';
import api from '../../utils/axios';
import FeedSidebar from './FeedSidebar';
import AgentGrid from './AgentGrid';
import AgentDashboard from './AgentDashboard';
import Dashboard from '../scout/Dashboard';
import BrandVoiceSettings from '../scout/BrandVoiceSettings';
import WatchWebsiteModal from './WatchWebsiteModal';
import PillBuilder from '../search/PillBuilder';
import PrecisionPanel from '../search/PrecisionPanel';
import RefineCoPilot from '../search/RefineCoPilot';
import RefineButton from '../search/RefineButton';
import RefinePanelOpener from '../search/RefinePanelOpener';
import SearchTabBar from '../search/SearchTabBar';
import LoadMoreFooter from '../search/LoadMoreFooter';
import { SearchProvider, useSearchContext } from '../../contexts/SearchContext';
import SplitPaneLayout, { useSplitPane, SplitPaneToggle } from '../layout/SplitPaneLayout';
import { convertKeyElementsToFilters, extractKeyElementsForSimilar, getSimilarLookupExplanation } from '../../utils/extractKeyElements';
import RankingWeightControls from '../shared/RankingWeightControls';
import { useRankingWeights } from '../../hooks/useRankingWeights';
import { BarChart3, Settings, LogOut, MoreHorizontal, Activity } from 'lucide-react';
import { LeadCard, LEAD_STATUSES } from '../shared/LeadCard';
import { LeadDetailPanel } from '../shared/LeadDetailPanel';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useSearchPath } from '../../contexts/SearchPathContext';
import SearchBreadcrumbs from '../shared/SearchBreadcrumbs';
import { useToast } from '../shared/Toast';
import { getCurrentWeights } from '../../hooks/useRankingWeights';
import { useSignUpGate, SignUpGateModal } from '../shared/SignUpGate';
import { useKeyboardNavigation } from '../../hooks/useKeyboardNavigation';
import KeyboardShortcutsHelp from '../ui/KeyboardShortcutsHelp';
import Faststarts from '../shared/Faststarts';
import { useKeywordScoring } from '../../hooks/useKeywordScoring';
import OrderResultsBar from '../shared/OrderResultsBar';

const isScoutExperimental = process.env.REACT_APP_SCOUT_EXPERIMENTAL === 'true';

// SEARCH_MODES is now consolidated into AGENT_MODES above

// =============================================================================

class DebugErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error(`Error in ${this.props.name}:`, error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4 text-sm text-red-600">
          Something went wrong while rendering.
        </div>
      );
    }
    return this.props.children;
  }
}
// FIELD CONFIGURATION - All available fields users can toggle
// Organized by search type and category
// =============================================================================

const PEOPLE_FIELDS = {
  core: {
    label: 'Core Profile',
    description: 'Essential contact information',
    fields: [
      { id: 'firstName', label: 'First Name', default: true },
      { id: 'lastName', label: 'Last Name', default: true },
      { id: 'fullName', label: 'Full Name', default: true },
      { id: 'jobTitle', label: 'Job Title', default: true },
      { id: 'email', label: 'Email Address', default: true },
      { id: 'phone', label: 'Direct Phone', default: true },
      { id: 'mobile', label: 'Mobile Phone', default: false },
      { id: 'linkedinUrl', label: 'LinkedIn Profile', default: true },
    ]
  },
  professional: {
    label: 'Professional Details',
    description: 'Role and career information',
    fields: [
      { id: 'managementLevel', label: 'Management Level', default: true },
      { id: 'jobFunction', label: 'Job Function', default: false },
      { id: 'yearsExperience', label: 'Years of Experience', default: true },
      { id: 'education', label: 'Education History', default: true },
      { id: 'employmentHistory', label: 'Employment History', default: true },
      { id: 'skills', label: 'Skills & Expertise', default: true },
      { id: 'certifications', label: 'Certifications', default: false },
    ]
  },
  location: {
    label: 'Location',
    description: 'Geographic information',
    fields: [
      { id: 'city', label: 'City', default: true },
      { id: 'state', label: 'State/Region', default: true },
      { id: 'country', label: 'Country', default: false },
      { id: 'metroArea', label: 'Metro Area', default: false },
      { id: 'zipCode', label: 'Postal Code', default: false },
      { id: 'timezone', label: 'Timezone', default: false },
    ]
  },
  company: {
    label: 'Current Company',
    description: 'Information about their employer',
    fields: [
      { id: 'companyName', label: 'Company Name', default: true },
      { id: 'companyIndustry', label: 'Industry', default: true },
      { id: 'companySize', label: 'Company Size', default: false },
      { id: 'companyRevenue', label: 'Revenue Range', default: false },
      { id: 'companyFunding', label: 'Funding Stage', default: false },
      { id: 'companyWebsite', label: 'Company Website', default: false },
      { id: 'companyLinkedin', label: 'Company LinkedIn', default: false },
      { id: 'companyHeadquarters', label: 'HQ Location', default: false },
    ]
  },
  social: {
    label: 'Social & Web',
    description: 'Online presence',
    fields: [
      { id: 'twitter', label: 'Twitter/X Profile', default: false },
      { id: 'github', label: 'GitHub Profile', default: false },
      { id: 'personalWebsite', label: 'Personal Website', default: false },
      { id: 'otherUrls', label: 'Other Web Links', default: false },
      { id: 'picture', label: 'Profile Photo', default: false },
    ]
  },
  metadata: {
    label: 'Data Quality',
    description: 'Accuracy and freshness indicators',
    fields: [
      { id: 'contactAccuracyScore', label: 'Contact Accuracy Score', default: false },
      { id: 'lastUpdated', label: 'Last Updated Date', default: false },
      { id: 'dataSource', label: 'Data Source', default: false },
      { id: 'verificationStatus', label: 'Verification Status', default: false },
    ]
  },
  agent: {
    label: 'AI Insights',
    description: 'Agent-powered enrichment fields',
    fields: [
      { id: 'reasonToCall', label: 'Reason to Call', default: true },
      { id: 'matchScore', label: 'Match Score', default: true },
      { id: 'companyJobs', label: 'Company Open Jobs', default: false },
      { id: 'companyNews', label: 'Company Recent News', default: false },
      { id: 'linkedinActivity', label: 'LinkedIn Activity', default: false },
      { id: 'demandScore', label: 'Demand Score', default: false },
    ]
  },
};

const COMPANY_FIELDS = {
  core: {
    label: 'Company Basics',
    description: 'Essential company information',
    fields: [
      { id: 'name', label: 'Company Name', default: true },
      { id: 'website', label: 'Website', default: true },
      { id: 'industry', label: 'Industry', default: true },
      { id: 'description', label: 'Description', default: true },
      { id: 'logo', label: 'Company Logo', default: false },
    ]
  },
  size: {
    label: 'Size & Scale',
    description: 'Company size metrics',
    fields: [
      { id: 'employeeCount', label: 'Employee Count', default: true },
      { id: 'employeeRange', label: 'Employee Range', default: false },
      { id: 'revenue', label: 'Annual Revenue', default: false },
      { id: 'revenueRange', label: 'Revenue Range', default: true },
      { id: 'marketCap', label: 'Market Cap', default: false },
    ]
  },
  location: {
    label: 'Location',
    description: 'Geographic presence',
    fields: [
      { id: 'headquarters', label: 'Headquarters', default: true },
      { id: 'city', label: 'City', default: true },
      { id: 'state', label: 'State/Region', default: true },
      { id: 'country', label: 'Country', default: true },
      { id: 'address', label: 'Full Address', default: false },
      { id: 'officeLocations', label: 'Office Locations', default: false },
    ]
  },
  funding: {
    label: 'Funding & Investment',
    description: 'Capital and investment data',
    fields: [
      { id: 'fundingStage', label: 'Funding Stage', default: true },
      { id: 'totalFunding', label: 'Total Funding', default: false },
      { id: 'lastFundingDate', label: 'Last Funding Date', default: false },
      { id: 'lastFundingAmount', label: 'Last Funding Amount', default: false },
      { id: 'investors', label: 'Key Investors', default: false },
      { id: 'ipoStatus', label: 'IPO Status', default: false },
    ]
  },
  contacts: {
    label: 'Key Contacts',
    description: 'Leadership and decision makers',
    fields: [
      { id: 'ceo', label: 'CEO/Founder', default: false },
      { id: 'executives', label: 'Executive Team', default: false },
      { id: 'hiringManagers', label: 'Hiring Managers', default: false },
      { id: 'hrContacts', label: 'HR Contacts', default: false },
      { id: 'companyPhone', label: 'Company Phone', default: false },
      { id: 'companyEmail', label: 'General Email', default: false },
    ]
  },
  social: {
    label: 'Social & Web',
    description: 'Online presence',
    fields: [
      { id: 'linkedin', label: 'LinkedIn Page', default: true },
      { id: 'twitter', label: 'Twitter/X', default: false },
      { id: 'facebook', label: 'Facebook', default: false },
      { id: 'crunchbase', label: 'Crunchbase', default: false },
      { id: 'glassdoor', label: 'Glassdoor', default: false },
    ]
  },
  signals: {
    label: 'Hiring Signals',
    description: 'Growth and hiring indicators',
    fields: [
      { id: 'openJobs', label: 'Open Job Count', default: true },
      { id: 'hiringTrend', label: 'Hiring Trend', default: false },
      { id: 'recentHires', label: 'Recent Hires', default: false },
      { id: 'departmentGrowth', label: 'Department Growth', default: false },
      { id: 'techStack', label: 'Tech Stack', default: false },
    ]
  },
  metadata: {
    label: 'Data Quality',
    description: 'Accuracy and sources',
    fields: [
      { id: 'lastUpdated', label: 'Last Updated', default: false },
      { id: 'dataSource', label: 'Data Source', default: false },
      { id: 'verificationStatus', label: 'Verification Status', default: false },
    ]
  },
  agent: {
    label: 'AI Insights',
    description: 'Agent-powered enrichment fields',
    fields: [
      { id: 'companyContacts', label: 'Decision Makers', default: true },
      { id: 'hiringIntent', label: 'Hiring Intent Score', default: false },
      { id: 'companyNews', label: 'Company Recent News', default: false },
      { id: 'expansionSignals', label: 'Expansion Signals', default: false },
      { id: 'competitorActivity', label: 'Competitor Activity', default: false },
    ]
  },
};

const JOBS_NEWS_FIELDS = {
  job: {
    label: 'Job Details',
    description: 'Job posting information',
    fields: [
      { id: 'jobTitle', label: 'Job Title', default: true },
      { id: 'department', label: 'Department', default: true },
      { id: 'location', label: 'Location', default: true },
      { id: 'remoteType', label: 'Remote/Hybrid/Onsite', default: true },
      { id: 'salary', label: 'Salary Range', default: false },
      { id: 'experienceLevel', label: 'Experience Level', default: true },
      { id: 'employmentType', label: 'Full-time/Part-time/Contract', default: false },
      { id: 'postedDate', label: 'Posted Date', default: true },
      { id: 'description', label: 'Job Description', default: false },
      { id: 'requirements', label: 'Requirements', default: false },
      { id: 'benefits', label: 'Benefits', default: false },
    ]
  },
  source: {
    label: 'Source & Links',
    description: 'Where the job was found',
    fields: [
      { id: 'sourceUrl', label: 'Original Posting URL', default: true },
      { id: 'sourcePlatform', label: 'Source Platform', default: true },
      { id: 'applyUrl', label: 'Apply Link', default: true },
      { id: 'companyCareerPage', label: 'Company Careers Page', default: false },
    ]
  },
  company: {
    label: 'Hiring Company',
    description: 'Company posting the job',
    fields: [
      { id: 'companyName', label: 'Company Name', default: true },
      { id: 'companyIndustry', label: 'Industry', default: true },
      { id: 'companySize', label: 'Company Size', default: false },
      { id: 'companyWebsite', label: 'Company Website', default: false },
      { id: 'companyLinkedin', label: 'Company LinkedIn', default: false },
    ]
  },
  news: {
    label: 'News & Signals',
    description: 'Related news and market intelligence',
    fields: [
      { id: 'newsHeadlines', label: 'Recent News Headlines', default: false },
      { id: 'newsSource', label: 'News Source', default: false },
      { id: 'newsDate', label: 'News Date', default: false },
      { id: 'newsUrl', label: 'News Article Link', default: false },
      { id: 'fundingNews', label: 'Funding Announcements', default: false },
      { id: 'expansionNews', label: 'Expansion/Growth News', default: false },
      { id: 'leadershipNews', label: 'Leadership Changes', default: false },
    ]
  },
  metadata: {
    label: 'Data Quality',
    description: 'Freshness and sources',
    fields: [
      { id: 'lastScraped', label: 'Last Scraped', default: false },
      { id: 'isActive', label: 'Still Active', default: true },
      { id: 'duplicateCount', label: 'Duplicate Postings', default: false },
    ]
  },
  agent: {
    label: 'AI Insights',
    description: 'Agent-powered enrichment fields',
    fields: [
      { id: 'hiringManager', label: 'Inferred Hiring Manager', default: true },
      { id: 'teamSize', label: 'Estimated Team Size', default: false },
      { id: 'budgetIndicators', label: 'Budget Indicators', default: false },
      { id: 'urgencyScore', label: 'Urgency Score', default: false },
      { id: 'companyGrowth', label: 'Company Growth Signals', default: false },
    ]
  },
};

// Helper to get default enabled fields for a field config
const getDefaultFields = (fieldConfig) => {
  const defaults = {};
  Object.values(fieldConfig).forEach(category => {
    category.fields.forEach(field => {
      defaults[field.id] = field.default;
    });
  });
  return defaults;
};

// LEAD_STATUSES is now imported from ../shared/LeadCard

// Custom field types for SERP enrichment
const CUSTOM_FIELD_TYPES = [
  { id: 'news', label: 'Recent News', icon: Newspaper, placeholder: '{company} latest news funding' },
  { id: 'jobs', label: 'Open Jobs', icon: Briefcase, placeholder: '{company} open jobs careers' },
  { id: 'linkedin', label: 'LinkedIn Activity', icon: Linkedin, placeholder: '{name} {company} linkedin' },
  { id: 'web', label: 'Web Search', icon: Globe, placeholder: '{company} {industry} market' },
  { id: 'custom', label: 'Custom Query', icon: Wand2, placeholder: 'Enter your search query...' },
];

// Core search modes - Ordered: People FIRST (ContactOut-powered), then Company, Signals, Jobs
const AGENT_MODES = [
  {
    id: 'people',
    name: 'People',
    icon: Users,
    color: 'from-[var(--eq-purple)] to-[var(--eq-teal)]',
    placeholder: 'Clinical operations managers in medtech',
    searchType: 'people'
  },
  {
    id: 'company',
    name: 'Companies',
    icon: Building2,
    color: 'from-indigo-500 to-indigo-600',
    placeholder: 'Biotech executives, VP and above, United States',
    searchType: 'people'
  },
  {
    id: 'demand',
    name: 'Signals',
    icon: TrendingUp,
    color: 'from-amber-500 to-amber-600',
    placeholder: 'Series B fintech last 30 days',
    searchType: 'demand',
    hasSubmodes: true // Enables signal type selector
  },
  {
    id: 'jobs',
    name: 'Jobs',
    icon: Briefcase,
    color: 'from-blue-500 to-blue-600',
    placeholder: 'Senior React roles in NYC',
    searchType: 'jobs'
  },
];

// Signal sub-modes for staffing agencies - DEX: 5-word max labels
// Maps to ZoomInfo Scoop Types for precise signal filtering
const SIGNAL_MODES = [
  {
    id: 'all',
    name: 'All Signals',
    icon: TrendingUp,
    color: 'from-amber-500 to-amber-600',
    placeholder: 'Funding, expansion, hiring news',
    scoopTypes: [2, 8, 11, 19, 21], // All hiring-relevant types
    hiringPrediction: true,
    description: 'All demand signals'
  },
  {
    id: 'funding',
    name: 'Recent Funding',
    icon: DollarSign,
    color: 'from-emerald-500 to-emerald-600',
    placeholder: 'Series B fintech Bay Area',
    scoopTypes: [2], // Funding
    hiringPrediction: true,
    description: 'Funded companies hire fast'
  },
  {
    id: 'fda',
    name: 'FDA & Biotech',
    icon: FlaskConical,
    color: 'from-teal-500 to-cyan-600',
    placeholder: 'FDA approval oncology pharma',
    scoopTypes: [9, 21], // Product Launch + Project (covers approvals)
    hiringPrediction: true,
    description: 'Approvals trigger scale-up',
    // FDA-specific keywords to enhance search
    searchEnhancement: 'FDA approval OR "FDA approved" OR "drug approval" OR "510(k)" OR "Phase 3" OR "clinical trial" OR "NDA" OR "BLA"'
  },
  {
    id: 'expansion',
    name: 'New Facilities',
    icon: MapPin,
    color: 'from-blue-500 to-blue-600',
    placeholder: 'Manufacturing plants Texas',
    scoopTypes: [8], // Facilities Relocation / Expansion
    hiringPrediction: true,
    description: 'Expansion means hiring'
  },
  {
    id: 'projects',
    name: 'Active Projects',
    icon: Briefcase,
    color: 'from-purple-500 to-purple-600',
    placeholder: 'Healthcare IT implementations',
    scoopTypes: [21, 9], // Project + Product Launch
    hiringPrediction: false,
    description: 'Projects need contractors'
  },
  {
    id: 'leadership',
    name: 'Leadership Changes',
    icon: Crown,
    color: 'from-amber-500 to-amber-600',
    placeholder: 'New CTO biotech companies',
    scoopTypes: [19, 13], // Executive Move + New Hire
    hiringPrediction: true,
    description: 'New leaders build teams'
  },
  {
    id: 'layoffs',
    name: 'Talent Available',
    icon: Users,
    color: 'from-slate-500 to-slate-600',
    placeholder: 'Tech layoffs Bay Area',
    scoopTypes: [17, 16], // Layoffs + Left Company
    hiringPrediction: false,
    description: 'Find displaced talent'
  },
];

export default function FeedHome() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const urlParams = new URLSearchParams(routerLocation.search);
  const urlQuery = urlParams.get('q') || urlParams.get('query') || '';
  const urlMode = urlParams.get('mode') || '';
  const urlAuto = urlParams.get('auto') === '1';
  const inviteToken = urlParams.get('invite');
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const { user, logout, isAuthenticated, loginWithRedirect } = useAuth();
  const { subscription, getUsedCredits, getDailyLimit, refreshSubscription } = useSubscription();
  const toast = useToast();
  const { addStep, path: searchPath, canGoBack, goBack } = useSearchPath();
  
  // Guest mode handling - gate card interactions for non-authenticated users
  const { isGuest, showGate, gateFeature, requireAuth, closeGate, SignUpGate } = useSignUpGate();

  // EQ inReach promo modal — once per session
  const [showInReachPromo, setShowInReachPromo] = useState(() => {
    if (!isAuthenticated) return false;
    return !sessionStorage.getItem('eqinreach_promo_seen');
  });
  const dismissInReachPromo = () => {
    sessionStorage.setItem('eqinreach_promo_seen', '1');
    setShowInReachPromo(false);
  };

  // Handle invitation: redirect to dedicated landing page for unauthenticated,
  // accept directly for authenticated users (backward compat for old /app?invite=TOKEN links)
  useEffect(() => {
    if (!inviteToken) return;
    if (!isAuthenticated) {
      navigate(`/invite/${inviteToken}`, { replace: true });
      return;
    }
    const accept = async () => {
      try {
        const { acceptInvitation } = await import('../../services/subscriptionService');
        const result = await acceptInvitation(inviteToken);
        if (result.success) {
          const orgName = result.orgName || "the team";
          await refreshSubscription();
          toast?.success?.(`Welcome to ${orgName}! You now share credits and searches with your team.`, { duration: 5000 });
          navigate('/app/settings?tab=workspace', { replace: true });
        }
      } catch (err) {
        console.error('Invitation acceptance failed:', err);
        toast?.error?.('Invitation could not be accepted. It may have expired.');
      }
    };
    accept();
  }, [inviteToken, isAuthenticated]);
  
  // Agent loading state - shows loading indicator on specific agent
  const [loadingAgentId, setLoadingAgentId] = useState(null);
  
  // Home view mode - 'dashboard' shows review-first agent cards, 'search' shows search hero
  // Default to dashboard only in experimental mode; production defaults to search-first
  const [homeView, setHomeView] = useState(
    urlQuery ? 'search' : (isScoutExperimental ? 'dashboard' : 'search')
  );
  
  // Reset homeView when navigating to /app without a query
  useEffect(() => {
    if (routerLocation.pathname === '/app' && !urlQuery && !urlAuto) {
      setHomeView(isScoutExperimental ? 'dashboard' : 'search');
    }
  }, [routerLocation.pathname, urlQuery, urlAuto]);
  
  // User menu state
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  // Signal Preview Modal State
  const [signalPreview, setSignalPreview] = useState(null);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Quick search event handler - triggered by contextual "rabbit hole" clicks
  // Use refs so the event handler always has access to the latest functions
  const handleSearchRef = useRef(null);
  const setQueryRef = useRef(null);
  const setSearchModeRef = useRef(null);
  
  useEffect(() => {
    const handler = async (event) => {
      const { query: quickQuery, mode, autoExecute } = event.detail || {};
      if (quickQuery) {
        // Update query and mode state
        setQueryRef.current?.(quickQuery);
        if (mode && setSearchModeRef.current) setSearchModeRef.current(mode);
        
        // Auto-execute the search after a brief delay to let state update
        if (autoExecute) {
          setTimeout(() => {
            // Call handleSearch directly via ref (no form needed!)
            if (handleSearchRef.current) {
              handleSearchRef.current(quickQuery, mode);
            }
          }, 150);
        }
      }
    };
    
    window.addEventListener('eq:quick-search', handler);
    return () => window.removeEventListener('eq:quick-search', handler);
  }, []);
  
  // Listen for brand voice open event (from BulkOutreachModal)
  useEffect(() => {
    const handleOpenBrandVoice = () => {
      setShowBrandVoice(true);
    };
    
    window.addEventListener('eq:open-brand-voice', handleOpenBrandVoice);
    return () => window.removeEventListener('eq:open-brand-voice', handleOpenBrandVoice);
  }, []);

  // Listen for focus search event (from GlobalHeader "New" button)
  useEffect(() => {
    const handleFocusSearch = () => {
      // Switch to search view if in dashboard mode
      setHomeView('search');
      // Small delay to ensure component is mounted and input is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 150);
    };
    
    window.addEventListener('eq:focus-search', handleFocusSearch);
    return () => window.removeEventListener('eq:focus-search', handleFocusSearch);
  }, []);

  useEffect(() => {
    if (!urlQuery) return;
    setQueryRef.current?.(urlQuery);
    if (urlMode && setSearchModeRef.current) setSearchModeRef.current(urlMode);
    if (urlAuto) {
      setTimeout(() => {
        handleSearchRef.current?.(urlQuery, urlMode || undefined);
      }, 150);
    }
  }, [routerLocation.search, urlAuto, urlMode, urlQuery]);

  
  // State - Check for prefilled query from navigation state (e.g., from FeedWorkspace "Edit" button)
  const [query, setQuery] = useState(routerLocation.state?.query || urlQuery || '');
  const [isSearching, setIsSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState({ step: 0, message: '' }); // Scout progress state
  const [feeds, setFeeds] = useState([]);
  const [isLoadingFeeds, setIsLoadingFeeds] = useState(true);
  const [error, setError] = useState(null);
  
  // Search results state
  const [searchResults, setSearchResults] = useState([]);
  const [totalResultsCount, setTotalResultsCount] = useState(null); // Total available from ZoomInfo
  const [broadeningOptions, setBroadeningOptions] = useState([]); // Suggestions when results are low
  const [selectedLead, setSelectedLead] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [savedSearchId, setSavedSearchId] = useState(null);
  const [isEnrichingAll, setIsEnrichingAll] = useState(false); // For bulk contact enrichment
  
  // Pagination state for "Load More" functionality
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [lastSearchParams, setLastSearchParams] = useState(null); // Store params to fetch next page
  
  // Search options (wired to the full AI interpreter)
  // Map legacy mode values to new unified modes
  const normalizeModeId = (mode) => {
    if (mode === 'candidate' || mode === 'client') return 'people';
    return mode;
  };
  const [searchMode, setSearchMode] = useState(normalizeModeId(routerLocation.state?.mode || urlMode) || 'people');
  const [signalMode, setSignalMode] = useState('all'); // Signal sub-mode: 'all', 'funding', 'expansion', 'projects', 'leadership', 'layoffs'
  const [location, setLocation] = useState('');
  const [includeRemote, setIncludeRemote] = useState(false);
  // Precision search is always ON for People and Company modes (ContactOut-powered)
  const precisionSearch = searchMode === 'people' || searchMode === 'company';
  const [precisionRefinements, setPrecisionRefinements] = useState(null); // User refinements from PrecisionPanel
  // Company mode: filters alone are sufficient to search (no query text needed)
  const hasCompanyFilters = searchMode === 'company' && precisionRefinements && Object.values(precisionRefinements).some(v => v != null && v !== '' && (!Array.isArray(v) || v.length > 0));
  const [precisionParsedFields, setPrecisionParsedFields] = useState(null); // AI-parsed fields for PrecisionPanel
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Update refs for quick search handler access — normalize legacy mode IDs
  setSearchModeRef.current = (m) => setSearchMode(normalizeModeId(m) || 'people');
  setQueryRef.current = setQuery;
  
  // Pill-based search state
  const [showPillBuilder, setShowPillBuilder] = useState(true); // Show pill interpretation by default
  const [pillFilters, setPillFilters] = useState([]);
  const [pillOutputFields, setPillOutputFields] = useState([]);
  
  // Ranking weight controls
  const { weights: rankingWeights, setWeights: setRankingWeights } = useRankingWeights();
  
  // Search-first view state with collapsible agent grid
  const [showAgentGrid, setShowAgentGrid] = useState(false); // Hidden by default, show on click
  const [agentGridViewMode, setAgentGridViewMode] = useState('list'); // Default to 'list' for mobile-friendly view
  const [isSearchFocused, setIsSearchFocused] = useState(true); // Start focused on search
  
  // Field customization - which fields to include in results
  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [detectedSearchType, setDetectedSearchType] = useState('people'); // 'people', 'company', 'jobs'
  const [enabledPeopleFields, setEnabledPeopleFields] = useState(() => getDefaultFields(PEOPLE_FIELDS));
  const [enabledCompanyFields, setEnabledCompanyFields] = useState(() => getDefaultFields(COMPANY_FIELDS));
  const [enabledJobsFields, setEnabledJobsFields] = useState(() => getDefaultFields(JOBS_NEWS_FIELDS));
  
  // Custom SERP fields - user-defined fields that pull from web search
  const [customFields, setCustomFields] = useState([]);
  const [showCustomFieldModal, setShowCustomFieldModal] = useState(false);
  
  // Field ordering - which order fields appear in the profile
  const [fieldOrder, setFieldOrder] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  
  // View mode - list or kanban
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  
  // Ranking preset for re-sorting results
  const [rankingPreset, setRankingPreset] = useState('balanced');
  
  // Lead statuses for kanban
  const [leadStatuses, setLeadStatuses] = useState({});
  
  // Inline filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    company: '',
    title: '',
    industry: '',
  });

  // List upload & enrichment
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  
  // Refine state - now handled by SplitPaneLayout + RefineCoPilot
  const [refineQueryText, setRefineQueryText] = useState('');
  const [shouldOpenRefinePanel, setShouldOpenRefinePanel] = useState(false);
  const [refinePillFilters, setRefinePillFilters] = useState([]);
  const [refinePillOutputFields, setRefinePillOutputFields] = useState([]);
  const [listSize, setListSize] = useState(25);
  const [listMode, setListMode] = useState('short');
  const [loadedCount, setLoadedCount] = useState(25);
  const [hasMore, setHasMore] = useState(true);
  
  // Track if search has been modified since initial save
  const [isSearchModified, setIsSearchModified] = useState(false);
  const [enrichmentProgress, setEnrichmentProgress] = useState(null); // { total, completed, status }
  const [uploadedList, setUploadedList] = useState([]);
  
  // Gamification - celebrate every 10 leads processed
  const [celebration, setCelebration] = useState(null); // { type: 'milestone', count: 10 }
  const [processedCount, setProcessedCount] = useState({ liked: 0, archived: 0 });
  
  // Status filter - quickly view only liked, archived, or all leads
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'liked' | 'archived' | 'inbox'
  
  // Share modal
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  
  // Dashboard modal
  const [showDashboard, setShowDashboard] = useState(false);
  
  // Brand Voice Settings modal
  const [showBrandVoice, setShowBrandVoice] = useState(false);
  
  // Similar search trigger
  const [showSimilarSearch, setShowSimilarSearch] = useState(null); // company name or null

  // Watch Website Modal
  const [showWatchWebsiteModal, setShowWatchWebsiteModal] = useState(false);
  
  // New leads notification
  const [newLeadsNotification, setNewLeadsNotification] = useState(null); // { feedId, count, feedName }
  
  // Keyboard navigation state
  const [selectedLeadIndex, setSelectedLeadIndex] = useState(0);

  // Fetch existing feeds
  const fetchFeeds = useCallback(async () => {
    try {
      setIsLoadingFeeds(true);
      const response = await api.get('/api/saved-searches');
      if (response.data?.success && response.data?.savedSearches) {
        setFeeds(response.data.savedSearches);
      }
    } catch (err) {
      console.error('Failed to fetch feeds:', err);
    } finally {
      setIsLoadingFeeds(false);
    }
  }, []);

  useEffect(() => {
    // Only fetch feeds if user is authenticated
    if (isAuthenticated) {
      fetchFeeds();
    }
  }, [fetchFeeds, isAuthenticated]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Detect search type from query
  const detectSearchType = useCallback((q) => {
    const lowerQuery = q.toLowerCase();
    
    // JOBS-focused keywords - check FIRST since "jobs" is a strong signal
    // Match patterns like "CEO jobs", "jobs in SF", "find jobs", etc.
    const jobPatterns = [
      /\bjobs?\b/,         // "job" or "jobs" as standalone word
      /\bopen position/,   // "open position(s)"
      /\bjob opening/,     // "job opening(s)"
      /\bvacancy/,         // "vacancy" or "vacancies"
      /\bcareer page/,     // "career page(s)"
      /\bwho is hiring/,   // "who is hiring"
      /\bactively hiring/, // "actively hiring"
      /\bhiring for\b/,    // "hiring for [role]"
      /\broles? at\b/,     // "role at" or "roles at"
      /\bpositions? at\b/, // "position at" or "positions at"
    ];
    if (jobPatterns.some(pattern => pattern.test(lowerQuery))) {
      return 'jobs';
    }
    
    // People-focused keywords - titles and roles (for finding people, not job listings)
    // If searching for people (decision makers, contacts), it's a people search
    const peopleKeywords = [
      'cto', 'ceo', 'cfo', 'coo', 'cmo', 'cio', 'vp', 'director', 'manager', 'head of',
      'founder', 'co-founder', 'president', 'partner', 'executive', 'officer',
      'engineer', 'developer', 'designer', 'analyst', 'recruiter', 'lead',
      'candidate', 'candidates', 'talent', 'professional', 'professionals',
      'people who', 'someone who', 'anyone who', 'decision maker', 'decision makers',
      'hiring manager', 'hiring managers', 'leaders', 'sales rep', 'account executive',
      'find me', 'looking for', 'search for'
    ];
    if (peopleKeywords.some(kw => lowerQuery.includes(kw))) {
      return 'people';
    }
    
    // Company-focused keywords (only if no people/jobs context)
    const companyKeywords = ['company', 'companies', 'startup', 'startups', 'organization', 'firm', 'business', 
                             'businesses', 'enterprise', 'enterprises', 'corporation', 'corporations'];
    if (companyKeywords.some(kw => lowerQuery.includes(kw))) {
      return 'company';
    }
    
    // Default to people search (most common use case)
    return 'people';
  }, []);

  // Update detected search type based on the user's selected mode
  // DO NOT auto-switch modes - respect user's manual selection
  useEffect(() => {
    // Always use the mode's search type - user controls the mode selector
    const modeConfig = AGENT_MODES.find(m => m.id === searchMode);
    if (modeConfig?.searchType) {
      setDetectedSearchType(modeConfig.searchType);
    }
  }, [searchMode]);

  // Toggle a single field
  const toggleField = (fieldId, searchType) => {
    if (searchType === 'people') {
      setEnabledPeopleFields(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
    } else if (searchType === 'company') {
      setEnabledCompanyFields(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
    } else if (searchType === 'jobs') {
      setEnabledJobsFields(prev => ({ ...prev, [fieldId]: !prev[fieldId] }));
    }
  };

  // Toggle all fields in a category
  const toggleCategory = (categoryFields, enable, searchType) => {
    const updates = {};
    categoryFields.forEach(field => {
      updates[field.id] = enable;
    });
    
    if (searchType === 'people') {
      setEnabledPeopleFields(prev => ({ ...prev, ...updates }));
    } else if (searchType === 'company') {
      setEnabledCompanyFields(prev => ({ ...prev, ...updates }));
    } else if (searchType === 'jobs') {
      setEnabledJobsFields(prev => ({ ...prev, ...updates }));
    }
  };

  // Get current field config and enabled fields based on search type
  const getCurrentFieldConfig = () => {
    switch (detectedSearchType) {
      case 'company':
        return { config: COMPANY_FIELDS, enabled: enabledCompanyFields };
      case 'jobs':
        return { config: JOBS_NEWS_FIELDS, enabled: enabledJobsFields };
      default:
        return { config: PEOPLE_FIELDS, enabled: enabledPeopleFields };
    }
  };

  // =============================================================================
  // FILTERING & SORTING
  // Order: Inbox first (unreviewed), then Liked, then Archived at the very bottom
  // Archived leads slide out but stay at bottom so users can recover them
  // Status filter allows quick navigation to liked/archived leads
  // =============================================================================
  const filteredResults = React.useMemo(() => {
    if (!searchResults.length) return [];
    
    // Filter: apply user filters AND status filter
    const filtered = searchResults.filter(lead => {
      const data = lead.data || lead.lead_data || lead;
      const fullName = data.name || data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
      const jobTitle = (data.jobTitle || data.title || '').toLowerCase();
      const companyName = (data.companyName || data.company_name || '').toLowerCase();
      const location = (data.city || data.location || '').toLowerCase();
      const industry = (data.industry || data.companyIndustry || '').toLowerCase();
      
      // Status filter - show only leads with selected status
      const leadId = lead.id || lead.personId;
      const leadStatus = leadStatuses[leadId] || 'inbox';
      if (statusFilter !== 'all' && leadStatus !== statusFilter) {
        return false;
      }
      
      if (filters.search && !fullName.includes(filters.search.toLowerCase()) && 
          !jobTitle.includes(filters.search.toLowerCase()) && 
          !companyName.includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.location && !location.includes(filters.location.toLowerCase())) return false;
      if (filters.company && !companyName.includes(filters.company.toLowerCase())) return false;
      if (filters.title && !jobTitle.includes(filters.title.toLowerCase())) return false;
      if (filters.industry && !industry.includes(filters.industry.toLowerCase())) return false;
      
      return true;
    });
    
    // Sort: LIKED leads first (user's selections), then inbox (unprocessed), then archived at bottom
    return filtered.sort((a, b) => {
      const statusA = leadStatuses[a.id || a.personId] || 'inbox';
      const statusB = leadStatuses[b.id || b.personId] || 'inbox';
      
      // Priority: liked/shortlist (0) > inbox (1) > archived (2)
      // Liked leads are the user's selections - they should always be visible at the top
      const priority = { liked: 0, shortlist: 0, inbox: 1, archived: 2 };
      return (priority[statusA] ?? 1) - (priority[statusB] ?? 1);
    });
  }, [searchResults, filters, leadStatuses, statusFilter]);

  // Apply Quick Sort preset to results BEFORE keyword boosting
  const presetSortedResults = React.useMemo(() => {
    if (!filteredResults || filteredResults.length === 0) return filteredResults;
    if (rankingPreset === 'balanced') return filteredResults; // Default order from backend
    
    const sorted = [...filteredResults];
    
    const getFieldValue = (lead, field) => {
      const data = lead.data || lead.lead_data || lead;
      return data[field] || lead[field] || null;
    };
    
    const getManagementLevelScore = (level) => {
      if (!level) return 0;
      const levelStr = (typeof level === 'string' ? level : level.name || '').toLowerCase();
      // Higher score = more senior
      if (levelStr.includes('c-level') || levelStr.includes('ceo') || levelStr.includes('cfo') || levelStr.includes('cto')) return 100;
      if (levelStr.includes('vp') || levelStr.includes('vice president')) return 80;
      if (levelStr.includes('director')) return 60;
      if (levelStr.includes('manager') || levelStr.includes('senior')) return 40;
      return 20;
    };
    
    sorted.sort((a, b) => {
      const aData = a.data || a.lead_data || a;
      const bData = b.data || b.lead_data || b;
      
      switch (rankingPreset) {
        case 'qualityFirst': // Accuracy
          const aAccuracy = aData.contactAccuracyScore || aData.contact_accuracy_score || aData.accuracy || 0;
          const bAccuracy = bData.contactAccuracyScore || bData.contact_accuracy_score || bData.accuracy || 0;
          return bAccuracy - aAccuracy;
          
        case 'seniorityFirst': // Seniority
          const aLevel = getManagementLevelScore(aData.managementLevel || aData.management_level);
          const bLevel = getManagementLevelScore(bData.managementLevel || bData.management_level);
          return bLevel - aLevel;
          
        case 'locationFirst': // Location (prioritize those with location data)
          const aHasLoc = (aData.city || aData.state || aData.location) ? 1 : 0;
          const bHasLoc = (bData.city || bData.state || bData.location) ? 1 : 0;
          if (aHasLoc !== bHasLoc) return bHasLoc - aHasLoc;
          // Secondary: sort by original ranking
          return (b._rankingScore || 0) - (a._rankingScore || 0);
          
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [filteredResults, rankingPreset]);
  
  // Apply keyword boosting to prioritize results matching specific terms
  const {
    results: keywordScoredResults,
    matchCount: keywordMatchCount,
    roleMatchCount,
    companyMatchCount,
    roleKeywords,
    companyKeywords,
    addRoleKeyword,
    removeRoleKeyword,
    addCompanyKeyword,
    removeCompanyKeyword,
    clearAllKeywords,
    hasKeywords: hasBoostKeywordsActive,
  } = useKeywordScoring(presetSortedResults);
  
  // Get the display results - either keyword-boosted or preset-sorted
  const displayResults = React.useMemo(() => {
    if (hasBoostKeywordsActive && keywordScoredResults.length > 0) {
      return keywordScoredResults;
    }
    return presetSortedResults;
  }, [hasBoostKeywordsActive, keywordScoredResults, presetSortedResults]);

  // Group results by status for Kanban view
  const kanbanColumns = React.useMemo(() => {
    const columns = {};
    LEAD_STATUSES.forEach(status => {
      columns[status.id] = [];
    });
    
    displayResults.forEach(lead => {
      const status = leadStatuses[lead.id || lead.personId] || 'inbox';
      if (columns[status]) {
        columns[status].push(lead);
      } else {
        columns['inbox'].push(lead);
      }
    });
    
    return columns;
  }, [displayResults, leadStatuses]);

  // =============================================================================
  // KEYBOARD NAVIGATION - Power user shortcuts for fast lead processing
  // =============================================================================
  const { showHelp: showKeyboardHelp, setShowHelp: setShowKeyboardHelp } = useKeyboardNavigation({
    leads: displayResults,
    selectedIndex: selectedLeadIndex,
    onSelectIndex: (index) => {
      setSelectedLeadIndex(index);
      // Also update selectedLead to sync with the detail panel
      if (displayResults[index]) {
        setSelectedLead(displayResults[index]);
      }
    },
    onLike: (leadId) => {
      updateLeadStatus(leadId, 'liked');
    },
    onArchive: (leadId) => {
      updateLeadStatus(leadId, 'archived');
    },
    onOpenEmail: (email) => {
      window.location.href = `mailto:${email}`;
    },
    onCopyEmail: async (email) => {
      try {
        await navigator.clipboard.writeText(email);
        toast.success('Email copied', 'Email address copied to clipboard');
      } catch (err) {
        console.error('Failed to copy email:', err);
      }
    },
    onFocusSearch: () => {
      inputRef.current?.focus();
    },
    onClose: () => {
      setSelectedLead(null);
    },
    enabled: hasSearched && displayResults.length > 0 && !isSearching,
  });

  // Update lead status with gamification and persist to backend
  const updateLeadStatus = async (leadId, newStatus) => {
    const oldStatus = leadStatuses[leadId] || 'inbox';
    
    // Update UI immediately (optimistic update)
    setLeadStatuses(prev => ({ ...prev, [leadId]: newStatus }));
    
    // Only count if it's a new action (not already in this status)
    if (oldStatus !== newStatus) {
      setProcessedCount(prev => {
        const newCounts = { ...prev };
        // Count hot and okay as positive qualifications
        if (newStatus === 'hot' || newStatus === 'okay' || newStatus === 'liked') newCounts.liked += 1;
        if (newStatus === 'passed' || newStatus === 'archived') newCounts.archived += 1;
        
        const totalProcessed = newCounts.liked + newCounts.archived;
        
        // Celebrate every 10 leads!
        if (totalProcessed > 0 && totalProcessed % 10 === 0) {
          setCelebration({ 
            type: 'milestone', 
            count: totalProcessed,
            message: totalProcessed % 50 === 0 
              ? `Amazing! ${totalProcessed} leads processed!` 
              : totalProcessed % 20 === 0 
                ? `On fire! ${totalProcessed} leads!`
                : `Nice! ${totalProcessed} leads done!`
          });
          // Auto-dismiss celebration after 2.5 seconds
          setTimeout(() => setCelebration(null), 2500);
        }
        
        return newCounts;
      });
    }
    
    // Persist to backend
    // PATCH may 404 for precision-search leads that haven't been persisted yet —
    // that's OK because the enrich endpoint will create/find the lead record.
    api.patch(`/api/leads/${leadId}`, { status: newStatus }).catch(err => {
      console.warn('Lead patch failed (may not exist yet):', err.message);
    });

    // Add liked leads to the Reach draft queue (no AI pipeline — just saves the lead)
    if (newStatus === 'liked' || newStatus === 'hot') {
      api.post(`/api/leads/${leadId}/add-to-reach`).catch(err => {
        console.warn('Add to reach failed (lead still saved):', err.message);
      });
    }
  };

  // Add custom field
  const addCustomField = (field) => {
    setCustomFields(prev => [...prev, { ...field, id: `custom_${Date.now()}` }]);
    setShowCustomFieldModal(false);
  };

  // Remove custom field
  const removeCustomField = (fieldId) => {
    setCustomFields(prev => prev.filter(f => f.id !== fieldId));
  };

  // Reorder fields via drag and drop
  const handleFieldDragEnd = (result) => {
    if (!result.destination) return;
    
    const items = Array.from(fieldOrder);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    setFieldOrder(items);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({ search: '', location: '', company: '', title: '', status: '' });
  };

  // =============================================================================
  // DUPLICATE LIST - One-click clone
  // =============================================================================
  const handleDuplicateList = async (feedId) => {
    try {
      const feedToDuplicate = feeds.find(f => f.id === feedId);
      if (!feedToDuplicate) return;

      const response = await api.post('/api/saved-searches', {
        name: `${feedToDuplicate.name} (Copy)`,
        query: feedToDuplicate.query,
        searchParams: feedToDuplicate.searchParams,
        searchMode: normalizeModeId(feedToDuplicate.searchMode) || 'people',
      });

      if (response.data?.success) {
        fetchFeeds(); // Refresh the list
      }
    } catch (err) {
      console.error('Failed to duplicate list:', err);
    }
  };

  // =============================================================================
  // SAVE AS NEW - Create new agent from current refinement
  // =============================================================================
  const handleSaveAsNew = async (payload) => {
    try {
      const { query: refineQuery, mode, filters, outputFields, weights, listSize } = payload;
      
      // Generate a name from the query
      const searchName = refineQuery?.slice(0, 50) || 'New Search';
      
      // Backend expects specific field names: customName, naturalQuery, searchCriteria, leadType
      const response = await api.post('/api/saved-searches', {
        customName: searchName,
        naturalQuery: refineQuery || query,
        searchCriteria: {
          mode: mode || searchMode,
          filters,
          outputFields,
          weights,
          listSize,
        },
        leadType: mode || searchMode,
        resultsLimit: listSize || 25,
      });

      if (response.data?.success && (response.data?.savedSearch?.id || response.data?.savedSearchId)) {
        // Refresh feeds list
        fetchFeeds();
        // Navigate to the new saved search (handle both response formats)
        const newId = response.data?.savedSearch?.id || response.data?.savedSearchId;
        navigate(`/app/feed/${newId}`);
      }
    } catch (err) {
      console.error('Failed to save as new:', err);
    }
  };
  
  // =============================================================================
  // SHARE LIST - Generate shareable link with "Powered by EQ.app"
  // =============================================================================
  const handleShareList = async (feedId) => {
    try {
      const response = await api.post(`/api/saved-searches/${feedId}/share`);
      if (response.data?.shareLink) {
        setShareLink(response.data.shareLink);
        setShowShareModal(true);
      } else {
        // Generate a simple share link if API doesn't support it
        const shareUrl = `${window.location.origin}/shared/feed/${feedId}?ref=eq`;
        setShareLink(shareUrl);
        setShowShareModal(true);
      }
    } catch (err) {
      // Fallback to local share link
      const shareUrl = `${window.location.origin}/shared/feed/${feedId}?ref=eq`;
      setShareLink(shareUrl);
      setShowShareModal(true);
    }
  };

  // =============================================================================
  // ENRICH LIST - Upload and enrich with EQ.app
  // =============================================================================
  const handleEnrichList = async (file, selectedFields) => {
    try {
      // Parse the file (CSV or JSON)
      const text = await file.text();
      let records = [];
      
      if (file.name.endsWith('.csv')) {
        // Parse CSV
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        records = lines.slice(1).filter(l => l.trim()).map(line => {
          const values = line.split(',');
          const record = {};
          headers.forEach((header, i) => {
            record[header] = values[i]?.trim() || '';
          });
          return record;
        });
      } else if (file.name.endsWith('.json')) {
        records = JSON.parse(text);
      }

      if (records.length === 0) return;

      setUploadedList(records);
      setEnrichmentProgress({ total: records.length, completed: 0, status: 'enriching' });

      // Start background enrichment
      const enrichedResults = [];
      for (let i = 0; i < records.length; i++) {
        try {
          const record = records[i];
          const response = await api.post('/api/ai/enrich-contact', {
            firstName: record.firstname || record.first_name || record.name?.split(' ')[0] || '',
            lastName: record.lastname || record.last_name || record.name?.split(' ').slice(1).join(' ') || '',
            email: record.email || '',
            company: record.company || record.companyname || record.organization || '',
            linkedinUrl: record.linkedin || record.linkedinurl || record.profile_url || '',
            fieldsToEnrich: selectedFields,
          });

          if (response.data?.enrichedData) {
            enrichedResults.push({ ...record, ...response.data.enrichedData });
          } else {
            enrichedResults.push(record);
          }
        } catch (enrichErr) {
          enrichedResults.push(records[i]);
        }

        setEnrichmentProgress(prev => ({
          ...prev,
          completed: i + 1,
          status: i + 1 === records.length ? 'complete' : 'enriching'
        }));
      }

      // Update search results with enriched data
      setSearchResults(enrichedResults.map((data, idx) => ({
        id: `enriched_${idx}`,
        data,
        source: 'enriched'
      })));
      setHasSearched(true);
      setShowEnrichModal(false);

    } catch (err) {
      console.error('Enrichment failed:', err);
      setEnrichmentProgress(prev => prev ? { ...prev, status: 'error' } : null);
    }
  };

  // =============================================================================
  // TRIGGER SIMILAR SEARCH - From company click
  // =============================================================================
  const handleSimilarSearch = (companyName, searchType = 'people') => {
    if (!companyName) return;
    
    // Set up a search for similar people/companies
    if (searchType === 'people') {
      setQuery(`People at ${companyName}`);
      setSearchMode('people');
    } else if (searchType === 'company') {
      setQuery(`Companies similar to ${companyName}`);
      setSearchMode('company');
    }
    
    setShowSimilarSearch(null);
    // Trigger search after a brief delay to update state
    setTimeout(() => handleSearch(), 100);
  };

  // Copy share link to clipboard
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
  };

  // =============================================================================
  // EXPORT CSV - Download results as CSV with optional field groups
  // =============================================================================
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFieldGroups, setExportFieldGroups] = useState({
    extendedContact: false,
    careerHistory: false,
    companyDetails: false,
    formality: false,
  });
  const [isExporting, setIsExporting] = useState(false);

  const FIELD_GROUP_META = {
    extendedContact: { label: 'Extended Contact Info', description: 'Additional emails, GitHub, Twitter, LinkedIn headline' },
    careerHistory:   { label: 'Career History',        description: 'Skills, education, work history, languages' },
    companyDetails:  { label: 'Company Details',       description: 'Domain, phone, HQ location, company overview' },
    formality:       { label: 'Formality / Mail Merge', description: 'Salutation, middle name, suffix' },
  };

  const handleExportCSV = () => {
    if (displayResults.length === 0) return;
    if (searchMode === 'company' || searchMode === 'jobs') {
      doSimpleExport();
    } else {
      setShowExportModal(true);
    }
  };

  const doSimpleExport = () => {
    const headers = searchMode === 'company' 
      ? ['Company Name', 'Industry', 'Website', 'Location', 'Employees', 'Revenue']
      : ['Job Title', 'Company', 'Location', 'Salary', 'Posted Date', 'Source URL'];

    const rows = displayResults.map(result => {
      const data = result.data || result.lead_data || result;
      if (searchMode === 'company') {
        return [
          data.companyName || data.name || '',
          data.industry || '',
          data.website || '',
          data.city || data.location || '',
          data.employeeCount || data.employees || '',
          data.revenue || '',
        ];
      }
      return [
        data.jobTitle || data.title || '',
        data.companyName || data.company || '',
        data.location || '',
        data.salary || '',
        data.postedDate || '',
        data.sourceUrl || '',
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${query.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const doExportWithGroups = async (overrideGroups) => {
    setIsExporting(true);
    try {
      const includeGroups = overrideGroups !== undefined
        ? overrideGroups
        : Object.entries(exportFieldGroups)
            .filter(([, enabled]) => enabled)
            .map(([key]) => key);

      const enrichedContactsData = {};
      displayResults.forEach((result, idx) => {
        const data = result.data || result.lead_data || result;
        const id = data.id || data.person_id || data.personId || `lead_${idx}`;
        enrichedContactsData[id] = { ...data, enriched: true };
      });

      const selectedContacts = Object.keys(enrichedContactsData);

      const response = await api.post('/api/ai/export-leads', {
        selectedContacts,
        exportFormat: 'csv',
        sessionId: `feed_${Date.now()}`,
        searchQuery: query,
        enrichedContactsData,
        includeGroups,
      });

      if (response.data?.success) {
        const blob = new Blob([response.data.exportData], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = response.data.filename || `${query.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        setShowExportModal(false);
      }
    } catch (err) {
      console.error('Export failed:', err);
      const serverMsg = err?.response?.data?.error || '';
      if (err?.response?.status === 403 || serverMsg.toLowerCase().includes('upgrade')) {
        toast?.error?.('Upgrade required', serverMsg || 'Upgrade your plan to export leads.');
        window.dispatchEvent(new CustomEvent('eq_show_upgrade_modal', { detail: { reason: 'export' } }));
      } else {
        toast?.error?.('Export failed', 'Please try again.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  // =============================================================================
  // BULK ACTIONS - Select all, bulk status change, bulk email
  // =============================================================================
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  const toggleSelectAll = () => {
    if (selectedLeads.length === displayResults.length) {
      setSelectedLeads([]);
    } else {
      const newSelected = displayResults.map(r => r.id || r.personId);
      setSelectedLeads(newSelected);
    }
  };

  const handleBulkStatusChange = (newStatus) => {
    selectedLeads.forEach(leadId => {
      updateLeadStatus(leadId, newStatus);
    });
    setSelectedLeads([]);
    setShowBulkActions(false);
  };

  const [isBulkAddingToReach, setIsBulkAddingToReach] = useState(false);

  const handleBulkAddToReach = async () => {
    if (selectedLeads.length === 0) return;
    setIsBulkAddingToReach(true);
    let added = 0;
    for (const leadId of selectedLeads) {
      try {
        await api.post(`/api/leads/${leadId}/add-to-reach`);
        added++;
      } catch (err) {
        console.warn(`Failed to add lead ${leadId} to Reach:`, err.message);
      }
    }
    setIsBulkAddingToReach(false);
    toast.success(`${added} lead${added !== 1 ? 's' : ''} added to Reach`);
    setSelectedLeads([]);
    setShowBulkActions(false);
  };

  // =============================================================================
  // SAFETY: Global ESC key handler - close any open modal/dropdown
  // User should NEVER feel stuck - ESC always provides escape
  // =============================================================================
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        // Close in priority order: modals first, then dropdowns, then selections
        if (celebration) { setCelebration(null); return; }
        if (signalPreview) { setSignalPreview(null); return; }
        if (showShareModal) { setShowShareModal(false); return; }
        // Refine panel handled by SplitPaneLayout
        if (showDashboard) { setShowDashboard(false); return; }
        if (showBrandVoice) { setShowBrandVoice(false); return; }
        if (showFieldConfig) { setShowFieldConfig(false); return; }
        if (showCustomFieldModal) { setShowCustomFieldModal(false); return; }
        if (showEnrichModal) { setShowEnrichModal(false); return; }
        if (showUserMenu) { setShowUserMenu(false); return; }
        if (showMoreActions) { setShowMoreActions(false); return; }
        if (showMobileSidebar) { setShowMobileSidebar(false); return; }
        if (showBulkActions) { setShowBulkActions(false); setSelectedLeads([]); return; }
        if (selectedLead) { setSelectedLead(null); return; }
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [celebration, signalPreview, showShareModal, showDashboard, 
      showBrandVoice, showFieldConfig, showCustomFieldModal, showEnrichModal,
      showUserMenu, showMoreActions, 
      showMobileSidebar, showBulkActions, selectedLead]);

  // Listen for sidebar events
  useEffect(() => {
    const handleOpenRefine = () => {
      setShouldOpenRefinePanel(true);
    };
    const handleExportCSVEvent = () => {
      handleExportCSV();
    };
    const handleShareListEvent = () => {
      if (savedSearchId) {
        handleShareList(savedSearchId);
      }
    };
    const handleToggleBulkActions = () => {
      setShowBulkActions(!showBulkActions);
    };
    const handleRefreshFeed = () => {
      if (savedSearchId) {
        navigate(`/app/feed/${savedSearchId}`);
        setTimeout(() => window.location.reload(), 100);
      } else {
        window.location.reload();
      }
    };

    window.addEventListener('eq:open-refine', handleOpenRefine);
    window.addEventListener('eq:export-csv', handleExportCSVEvent);
    window.addEventListener('eq:share-list', handleShareListEvent);
    window.addEventListener('eq:toggle-bulk-actions', handleToggleBulkActions);
    window.addEventListener('eq:refresh-feed', handleRefreshFeed);

    return () => {
      window.removeEventListener('eq:open-refine', handleOpenRefine);
      window.removeEventListener('eq:export-csv', handleExportCSVEvent);
      window.removeEventListener('eq:share-list', handleShareListEvent);
      window.removeEventListener('eq:toggle-bulk-actions', handleToggleBulkActions);
      window.removeEventListener('eq:refresh-feed', handleRefreshFeed);
    };
  }, [savedSearchId, showBulkActions, navigate]);
  
  // Handle successful website watch creation
  const handleWatchWebsiteSuccess = (result) => {
    // Refresh feeds to show the new watched source
    fetchFeeds();
    
    // Show notification if leads were found immediately
    if (result?.leadsFound > 0) {
      setNewLeadsNotification({
        feedId: result.id,
        count: result.leadsFound,
        feedName: result.name || 'New Watch'
      });
      
      // Auto-dismiss after 8 seconds
      setTimeout(() => setNewLeadsNotification(null), 8000);
    }
    
    // Navigate to the new feed if leads were found
    if (result?.id && result?.leadsFound > 0) {
      navigate(`/app/feed/${result.id}`);
    }
  };

  // Search for leads - returns results inline
  // Routes to correct API based on search mode:
  // - candidate/client → EQ.app People Search
  // - company → EQ.app Company Search  
  // - jobs → EQ.app Jobs Search
  const handleSearch = async (overrideQuery = null, overrideMode = null) => {
    // Handle case where event object is passed (from onClick handlers)
    // If overrideQuery is not a string, ignore it and use state
    const isValidOverride = typeof overrideQuery === 'string';
    const searchQuery = isValidOverride ? overrideQuery : query;
    const effectiveMode = (isValidOverride && overrideMode) ? normalizeModeId(overrideMode) : searchMode;
    
    const isCompanyFilterSearch = effectiveMode === 'company' && precisionRefinements && Object.values(precisionRefinements).some(v => v != null && v !== '' && (!Array.isArray(v) || v.length > 0));
    if ((!searchQuery || !searchQuery.trim()) && !isCompanyFilterSearch) return;
    
    // Update state if valid string overrides were provided
    if (isValidOverride && overrideQuery !== query) setQuery(overrideQuery);
    if (isValidOverride && overrideMode && normalizeModeId(overrideMode) !== searchMode) setSearchMode(normalizeModeId(overrideMode));

    setIsSearching(true);
    setError(null);
    setSearchResults([]);
    setTotalResultsCount(null); // Reset total count for new search
    setBroadeningOptions([]); // Reset broadening suggestions
    setSelectedLead(null);
    setHasSearched(true);
    setCurrentPage(1); // Reset pagination for new search
    setLastSearchParams(null);
    
    // Scout progress messages - EQ.app branded, no partner mentions
    const progressMessages = {
      people: [
        { step: 1, message: 'Scout is thinking...' },
        { step: 2, message: 'Analyzing your criteria...' },
        { step: 3, message: 'Searching professional networks...' },
        { step: 4, message: 'Ranking best matches...' },
      ],
      company: [
        { step: 1, message: 'Scout is thinking...' },
        { step: 2, message: 'Searching target companies...' },
        { step: 3, message: 'Finding key contacts...' },
        { step: 4, message: 'Ranking best matches...' },
      ],
      jobs: [
        { step: 1, message: 'Scout is thinking...' },
        { step: 2, message: 'Scanning job boards...' },
        { step: 3, message: 'Finding open positions...' },
        { step: 4, message: 'Matching to your criteria...' },
      ],
      demand: [
        { step: 1, message: 'Scout is thinking...' },
        { step: 2, message: 'Reading industry news...' },
        { step: 3, message: 'Analyzing hiring signals...' },
        { step: 4, message: 'Calculating predictions...' },
      ],
    };

    try {
      // Use the selected mode for jobs/demand, otherwise use detected type
      // Jobs and demand modes should always use their specific API, not fall back to people
      // Company mode uses the same people/precision search pipeline (ContactOut)
      // Use effectiveMode (not searchMode) to handle overrides correctly
      const effectiveSearchType = (effectiveMode === 'jobs' || effectiveMode === 'demand') 
        ? effectiveMode 
        : effectiveMode === 'company' ? 'people' : detectedSearchType;
      
      // Start progress animation - Scout branded messaging
      // Use effectiveMode for message selection (preserves 'company'), fall back to effectiveSearchType
      const messages = progressMessages[effectiveMode] || progressMessages[effectiveSearchType] || progressMessages.people;
      let currentStep = 0;
      setSearchProgress(messages[0]); // Show first message immediately
      
      // Clear any existing interval
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
      }
      
      progressIntervalRef.current = setInterval(() => {
        currentStep++;
        if (currentStep < messages.length) {
          setSearchProgress(messages[currentStep]);
        } else {
          // Loop with varied "Still working..." messages
          const continueMessages = [
            'Scout is still working...',
            'Talking to other agents...',
            'Almost there...',
            'Gathering more results...',
          ];
          setSearchProgress({ 
            step: currentStep, 
            message: continueMessages[(currentStep - messages.length) % continueMessages.length] 
          });
        }
      }, 1200); // Progress every 1.2s for natural feel
      
      // Search routing determined
      
      
      // Get enabled fields for this search type
      const currentFields = effectiveSearchType === 'company' 
        ? enabledCompanyFields 
        : effectiveSearchType === 'jobs' 
          ? enabledJobsFields 
          : enabledPeopleFields;

      const outputFields = Object.entries(currentFields)
        .filter(([_, enabled]) => enabled)
        .map(([fieldId]) => fieldId);

      let response;
      let results = [];

      // Route to correct API based on detected search type (query content takes priority)
      if (effectiveSearchType === 'company') {
        // =============================================================================
        // COMPANY SEARCH - EQ.app Company API
        // =============================================================================
        // Executing company search
        response = await api.post('/api/ai/ai-company-search-simple', {
          query: searchQuery,
          rpp: 100,
          outputFields: outputFields,
        });

        // Extract company results - handle both enriched and basic API responses
        // The enriched endpoint returns { companies: [...] } directly
        // The basic endpoint returns { results: { data: [...] } }
        const companyData = response.data?.companies || response.data?.results?.data || response.data?.results?.companies || response.data?.results || [];
        
        if (Array.isArray(companyData) && companyData.length > 0) {
          results = companyData.map((company, idx) => ({
            id: company.id || `company_${idx}`,
            type: 'company',
            data: {
              // Core company info
              companyName: company.name || company.companyName,
              industry: Array.isArray(company.primaryIndustry) ? company.primaryIndustry.join(', ') : (company.primaryIndustry || company.industry || ''),
              website: company.website,
              location: company.city ? `${company.city}${company.state ? `, ${company.state}` : ''}${company.country ? `, ${company.country}` : ''}` : (company.location || ''),
              city: company.city,
              state: company.state,
              country: company.country,
              // Size & financials
              employeeCount: company.employeeCount || company.employees,
              revenue: company.revenue || company.annualRevenue,
              // Description & media
              description: company.description || company.shortDescription || '',
              logoUrl: company.logoUrl || company.logo || company.companyLogo,
              // Additional details
              yearFounded: company.yearFounded,
              phone: company.phone,
              // Social links
              linkedInUrl: company.linkedInUrl || company.linkedinUrl,
              twitterUrl: company.twitterUrl,
              facebookUrl: company.facebookUrl,
              // Stock info
              ticker: company.ticker,
              // Funding info
              fundingAmount: company.fundingAmount,
              latestFundingRound: company.latestFundingRound,
              // Industry codes
              naicsCode: company.naicsCode,
              sicCode: company.sicCode,
              type: company.type,
            }
          }));
          console.log(`✅ Mapped ${results.length} company results with enriched data`);
        }
        
        console.log('Company results structure:', response.data?.results);        // DEBUG: Log what logo fields are in the raw company data
        if (response.data?.results?.data?.[0]) {
          const sample = response.data.results.data[0];
          console.log('LOGO DEBUG - Raw company data sample:', {
            name: sample.name,
            logo: sample.logo,
            logoUrl: sample.logoUrl,
            companyLogo: sample.companyLogo,
            imageUrl: sample.imageUrl,
            allKeys: Object.keys(sample),
          });
        }

      } else if (effectiveSearchType === 'jobs') {
        // =============================================================================
        // JOBS SEARCH - Uses SerpAPI Google Jobs via pill-based search
        // =============================================================================
        // Executing jobs search
        
        const jobFilters = pillFilters.length > 0 ? pillFilters : [
          // Build basic job filters from query if no pills
          { id: 'keywords', apiField: 'q', pills: [{ id: 'kw-0', label: searchQuery, status: 'included', apiValue: searchQuery }] }
        ];
        
        response = await api.post('/api/ai/execute-pill-search', {
          filters: jobFilters,
          outputFields: outputFields,
          mode: 'jobs',
          rpp: 100,
        });

        // Extract job results from execute-pill-search or SERP response
        if (response.data?.success && Array.isArray(response.data?.results)) {
          // Capture total results count
          if (response.data.totalResults) {
            setTotalResultsCount(response.data.totalResults);
          }
          // From execute-pill-search endpoint (Google Jobs API)
          results = response.data.results.map((job, idx) => ({
            id: job.job_id || `job_${idx}`,
            type: 'job',
            data: {
              jobTitle: job.title,
              companyName: job.company_name || job.company,
              location: job.location,
              description: job.description || job.snippet,
              salary: job.salary || job.detected_extensions?.salary,
              postedDate: job.detected_extensions?.posted_at || job.posted,
              sourceUrl: job.apply_options?.[0]?.link || job.link || job.job_link,
              applyUrl: job.apply_options?.[0]?.link,
              sourcePlatform: job.via || 'Web',
              remoteType: job.detected_extensions?.work_from_home ? 'Remote' : 'On-site',
              employmentType: job.detected_extensions?.schedule_type,
              thumbnail: job.thumbnail,
              extensions: job.extensions || [],
            }
          }));
        } else if (Array.isArray(response.data?.jobs)) {
          results = response.data.jobs.map((job, idx) => ({
            id: `job_${idx}`,
            type: 'job',
            data: {
              jobTitle: job.title,
              companyName: job.company_name || job.company,
              location: job.location,
              description: job.description || job.snippet,
              salary: job.salary || job.detected_extensions?.salary,
              postedDate: job.posted || job.postedDate || job.detected_extensions?.posted_at,
              sourceUrl: job.applyLink || job.link || job.job_link,
              sourcePlatform: job.via || 'Web',
              remoteType: job.detected_extensions?.work_from_home ? 'Remote' : 'On-site',
              employmentType: job.employmentType || job.detected_extensions?.schedule_type,
            }
          }));
        } else if (Array.isArray(response.data?.companies)) {
          // SERP jobs search returns companies with job arrays
          results = response.data.companies.flatMap((company, cIdx) => 
            (company.jobs || []).map((job, jIdx) => ({
              id: `job_${cIdx}_${jIdx}`,
              type: 'job',
              data: {
                jobTitle: job.title,
                companyName: company.name || job.company,
                location: job.location,
                description: job.description,
                salary: job.salary,
                postedDate: job.postedDate,
                sourceUrl: job.applyLink,
                sourcePlatform: job.via || 'Web',
              }
            }))
          );
        } else if (Array.isArray(response.data?.results)) {
          // Fallback: map job results properly
          results = response.data.results.map((job, idx) => ({
            id: job.id || `job_${idx}`,
            type: 'job',
            data: {
              jobTitle: job.title || job.jobTitle || '',
              companyName: typeof job.company === 'string' ? job.company 
                : job.company?.name || job.companyName || '',
              location: job.location || '',
              description: job.description || '',
              salary: job.salary || job.detected_extensions?.salary || '',
              postedDate: job.postedDate || job.date || '',
              sourceUrl: job.applyLink || job.link || '',
              sourcePlatform: job.via || 'Web',
            }
          }));
        }

      } else if (effectiveSearchType === 'demand') {
        // =============================================================================
        // DEMAND SIGNALS SEARCH - Uses SerpAPI Google News via pill-based search
        // Same architecture as jobs search for consistency
        // Signal sub-modes map to ZoomInfo Scoop Types for precise filtering
        // =============================================================================
        // Executing demand signals search
        
        // Get the active signal mode configuration
        const activeSignalMode = SIGNAL_MODES.find(m => m.id === signalMode) || SIGNAL_MODES[0];
        
        const demandFilters = pillFilters.length > 0 ? pillFilters : [
          // Build basic demand filters from query if no pills
          { id: 'keywords', apiField: 'q', pills: [{ id: 'kw-0', label: searchQuery, status: 'included', apiValue: searchQuery }] }
        ];
        
        response = await api.post('/api/ai/execute-pill-search', {
          filters: demandFilters,
          outputFields: outputFields,
          mode: 'demand',
          signalMode: signalMode, // Pass the signal sub-mode
          scoopTypes: activeSignalMode.scoopTypes, // ZoomInfo scoop type IDs
          includeHiringPrediction: activeSignalMode.hiringPrediction, // Request ripple logic
          rpp: 100,
        });

        // Extract signal/news results from execute-pill-search
        if (response.data?.success && Array.isArray(response.data?.results)) {
          // Capture total results count
          if (response.data.totalResults) {
            setTotalResultsCount(response.data.totalResults);
          }
          results = response.data.results.map((article, idx) => ({
            id: article.id || `signal_${idx}`,
            type: 'signal',
            data: {
              signalTitle: article.title,
              signalSnippet: article.snippet || article.description,
              signalDate: article.date || article.published_date,
              // Ensure signalSource is always a string, never an object
              signalSource: typeof article.source === 'string' ? article.source 
                : article.source?.name || article.source?.title || '',
              sourceUrl: article.link || article.url,
              sourceLogo: article.source?.icon || article.thumbnail,
              companyName: typeof article.source === 'string' ? article.source
                : article.source?.name || article.title?.split(/[:\-–—]/)[0]?.trim() || '',
              // Signal type from mode selection (DEX: clear labels for staffing)
              signalType: activeSignalMode.name || 'News',
              signalModeId: signalMode,
              // Hiring prediction from ripple-logic (if included)
              hiringPrediction: article.hiringPrediction || null,
              demandZone: article.demandZone || article.hiringPrediction?.demandZone || null,
              expectedHires: article.hiringPrediction?.expectedHires || null,
              outreachTiming: article.outreachTiming || article.hiringPrediction?.outreachTiming || null,
              fundingAmount: article.fundingAmount || article.amount || null,
              // Only spread safe primitive fields, not nested objects
              thumbnail: article.thumbnail,
              highlight: article.highlight,
            }
          }));
        } else if (Array.isArray(response.data?.news_results)) {
          results = response.data.news_results.map((article, idx) => ({
            id: `signal_${idx}`,
            type: 'signal',
            data: {
              signalTitle: article.title,
              signalSnippet: article.snippet,
              signalDate: article.date,
              // Ensure signalSource is always a string, never an object
              signalSource: typeof article.source === 'string' ? article.source 
                : article.source?.name || article.source?.title || '',
              sourceUrl: article.link,
              sourceLogo: article.source?.icon || article.thumbnail,
              companyName: typeof article.source === 'string' ? article.source
                : article.source?.name || '',
              signalType: 'news',
            }
          }));
        } else if (Array.isArray(response.data?.results)) {
          // Fallback: sanitize any raw results to ensure no object children
          results = response.data.results.map((article, idx) => ({
            id: article.id || `signal_${idx}`,
            type: 'signal',
            data: {
              signalTitle: article.title || article.signalTitle || '',
              signalSnippet: article.snippet || article.signalSnippet || article.description || '',
              signalDate: article.date || article.signalDate || '',
              signalSource: typeof article.source === 'string' ? article.source 
                : article.source?.name || article.source?.title || article.signalSource || '',
              sourceUrl: article.link || article.sourceUrl || article.url || '',
              sourceLogo: article.source?.icon || article.sourceLogo || article.thumbnail || '',
              companyName: typeof article.source === 'string' ? article.source
                : article.source?.name || article.companyName || '',
              signalType: 'news',
            }
          }));
        }

      } else {
        // =============================================================================
        // PEOPLE SEARCH - EQ.app People API (candidates & clients)
        // Company mode uses this same pipeline — backend treats it as people search
        // =============================================================================
        // Get user's ranking weight preferences
        const rankingWeights = getCurrentWeights();
        // Map 'company' mode to 'people' for API calls (backend uses same pipeline)
        const apiMode = effectiveMode === 'company' ? 'people' : effectiveMode;
        
        if (precisionSearch) {
          // =================================================================
          // PILLS-FIRST PEOPLE SEARCH — interpret → merge user edits → execute
          // =================================================================
          console.log(`🎯 Pills-First People Search${effectiveMode === 'company' ? ' (Company Mode)' : ''}: interpret-to-pills → execute-pill-search`);
          
          // Seniority mapping: ContactOut values → ZoomInfo management levels
          const SENIORITY_TO_MGMT = {
            'Owner / Founder': 'C Level Exec',
            'CXO': 'C Level Exec',
            'Partner': 'VP Level Exec',
            'VP': 'VP Level Exec',
            'Head': 'Director',
            'Director': 'Director',
            'Manager': 'Manager',
            'Senior': 'Non Manager',
            'Entry': 'Non Manager',
            'Intern': 'Non Manager',
          };
          
          let searchFilters;
          let searchOutputFields;
          const refs = precisionRefinements || {};
          
          const hasQuery = searchQuery && searchQuery.trim();
          
          if (hasQuery) {
            // Step 1a: Query present — get structured pills from AI interpretation
            const pillResponse = await api.post('/api/ai/interpret-to-pills', {
              query: searchQuery,
              mode: apiMode,
              location: location || undefined,
            });
            searchFilters = pillResponse.data?.filters || [];
            searchOutputFields = pillResponse.data?.outputFields || [];
          } else {
            // Step 1b: No query (company mode filter-only search) — build pills directly from refinements
            console.log('🏢 Company mode: building filters from panel selections (no query)');
            searchFilters = [];
            searchOutputFields = [];
          }
          
          // Step 2: Merge user's PrecisionPanel edits into the pills
          // (When no query, this IS the entire filter set)
          
          // Merge job titles — add user's titles, mark all as included
          if (refs.jobTitles?.length > 0) {
            let titleFilter = searchFilters.find(f => f.id === 'jobTitle');
            if (!titleFilter) {
              titleFilter = { id: 'jobTitle', label: 'Job Title', apiField: 'jobTitle', type: 'multi-select', pills: [] };
              searchFilters.push(titleFilter);
            }
            const existing = new Set(titleFilter.pills.map(p => (p.apiValue || p.label || '').toLowerCase()));
            refs.jobTitles.forEach((title, i) => {
              if (!existing.has(title.toLowerCase())) {
                titleFilter.pills.push({ id: `user-jt-${i}`, label: title, status: 'included', apiValue: title });
              }
            });
            titleFilter.pills.forEach(p => { if (p.status === 'suggested') p.status = 'included'; });
          }
          
          // Merge excluded titles
          if (refs.excludeTitles?.length > 0) {
            let titleFilter = searchFilters.find(f => f.id === 'jobTitle');
            if (!titleFilter) {
              titleFilter = { id: 'jobTitle', label: 'Job Title', apiField: 'jobTitle', type: 'multi-select', pills: [] };
              searchFilters.push(titleFilter);
            }
            refs.excludeTitles.forEach((title, i) => {
              const existingPill = titleFilter.pills.find(p => (p.apiValue || p.label || '').toLowerCase() === title.toLowerCase());
              if (existingPill) {
                existingPill.status = 'excluded';
              } else {
                titleFilter.pills.push({ id: `user-excl-jt-${i}`, label: title, status: 'excluded', apiValue: title });
              }
            });
          }
          
          // Merge seniority → managementLevel pills
          if (refs.seniority?.length > 0) {
            let levelFilter = searchFilters.find(f => f.id === 'managementLevel');
            if (!levelFilter) {
              levelFilter = { id: 'managementLevel', label: 'Seniority', apiField: 'managementLevel', type: 'multi-select', pills: [] };
              searchFilters.push(levelFilter);
            }
            const targetLevels = new Set(refs.seniority.map(s => SENIORITY_TO_MGMT[s] || s));
            levelFilter.pills.forEach(p => {
              if (targetLevels.has(p.apiValue)) p.status = 'included';
              else if (p.status === 'included') p.status = 'suggested';
            });
            targetLevels.forEach(level => {
              if (!levelFilter.pills.find(p => p.apiValue === level)) {
                levelFilter.pills.push({ id: `user-ml-${level}`, label: level, status: 'included', apiValue: level });
              }
            });
          }
          
          // Merge locations
          if (refs.locations?.length > 0) {
            let locFilter = searchFilters.find(f => f.id === 'state' || f.id === 'location');
            if (!locFilter) {
              locFilter = { id: 'state', label: 'Location', apiField: 'locationState', type: 'multi-select', pills: [] };
              searchFilters.push(locFilter);
            }
            refs.locations.forEach((loc, i) => {
              if (!locFilter.pills.find(p => (p.apiValue || p.label || '').toLowerCase() === loc.toLowerCase())) {
                locFilter.pills.push({ id: `user-loc-${i}`, label: loc, status: 'included', apiValue: loc });
              }
            });
          }
          
          // Merge countries
          if (refs.countries?.length > 0) {
            let countryFilter = searchFilters.find(f => f.id === 'country');
            if (!countryFilter) {
              countryFilter = { id: 'country', label: 'Country', apiField: 'country', type: 'multi-select', pills: [] };
              searchFilters.push(countryFilter);
            }
            refs.countries.forEach((c, i) => {
              if (!countryFilter.pills.find(p => (p.apiValue || p.label || '').toLowerCase() === c.toLowerCase())) {
                countryFilter.pills.push({ id: `user-co-${i}`, label: c, status: 'included', apiValue: c });
              }
            });
          }
          
          // Merge industries
          if (refs.industries?.length > 0) {
            let indFilter = searchFilters.find(f => f.id === 'industryCodes');
            if (!indFilter) {
              indFilter = { id: 'industryCodes', label: 'Industry', apiField: 'industryCodes', type: 'multi-select', pills: [] };
              searchFilters.push(indFilter);
            }
            refs.industries.forEach((ind, i) => {
              if (!indFilter.pills.find(p => (p.apiValue || p.label || '').toLowerCase() === ind.toLowerCase())) {
                indFilter.pills.push({ id: `user-ind-${i}`, label: ind, status: 'included', apiValue: ind });
              }
            });
          }
          
          // Merge headcount / company size
          if (refs.headcount?.length > 0) {
            let sizeFilter = searchFilters.find(f => f.id === 'employeeCountRange');
            if (!sizeFilter) {
              sizeFilter = { id: 'employeeCountRange', label: 'Company Size', apiField: 'employeeCountRange', type: 'multi-select', pills: [] };
              searchFilters.push(sizeFilter);
            }
            refs.headcount.forEach((size, i) => {
              if (!sizeFilter.pills.find(p => p.apiValue === size)) {
                sizeFilter.pills.push({ id: `user-hc-${i}`, label: size, status: 'included', apiValue: size });
              }
            });
          }
          
          // Merge departments
          if (refs.departments?.length > 0) {
            let deptFilter = searchFilters.find(f => f.id === 'jobFunction');
            if (!deptFilter) {
              deptFilter = { id: 'jobFunction', label: 'Department', apiField: 'jobFunction', type: 'multi-select', pills: [] };
              searchFilters.push(deptFilter);
            }
            refs.departments.forEach((dept, i) => {
              if (!deptFilter.pills.find(p => (p.apiValue || p.label || '').toLowerCase() === dept.toLowerCase())) {
                deptFilter.pills.push({ id: `user-dept-${i}`, label: dept, status: 'included', apiValue: dept });
              }
            });
          }
          
          // Merge companies
          if (refs.companies?.length > 0) {
            let compFilter = searchFilters.find(f => f.id === 'companyName');
            if (!compFilter) {
              compFilter = { id: 'companyName', label: 'Company', apiField: 'companyName', type: 'multi-select', pills: [] };
              searchFilters.push(compFilter);
            }
            refs.companies.forEach((co, i) => {
              compFilter.pills.push({ id: `user-co-${i}`, label: co, status: 'included', apiValue: co });
            });
          }
          
          // Merge skills as keyword if present
          if (refs.skills?.length > 0 || refs.keyword) {
            const keywordParts = [];
            if (refs.skills?.length > 0) keywordParts.push(...refs.skills);
            if (refs.keyword) keywordParts.push(refs.keyword);
            let kwFilter = searchFilters.find(f => f.id === 'keyword');
            if (!kwFilter) {
              kwFilter = { id: 'keyword', label: 'Keywords', apiField: 'keyword', type: 'text', pills: [] };
              searchFilters.push(kwFilter);
            }
            keywordParts.forEach((kw, i) => {
              if (!kwFilter.pills.find(p => (p.apiValue || p.label || '').toLowerCase() === kw.toLowerCase())) {
                kwFilter.pills.push({ id: `user-kw-${i}`, label: kw, status: 'included', apiValue: kw });
              }
            });
          }
          
          // Store the merged pills so the PillBuilder/Refine panel has them
          setPillFilters(searchFilters);
          setPillOutputFields(searchOutputFields);
          
          // Step 3: Execute the pill-based search
          console.log('🔍 Executing pill search with merged filters:', searchFilters.map(f => ({ id: f.id, pills: f.pills?.length })));
          response = await api.post('/api/ai/execute-pill-search', {
            filters: searchFilters,
            outputFields: searchOutputFields,
            mode: apiMode,
            rpp: 100,
            rankingWeights: rankingWeights,
            query: searchQuery || undefined,
          });
          
          if (Array.isArray(response.data?.results)) {
            results = response.data.results;
          }
          
          if (response.data?.totalResults) {
            setTotalResultsCount(response.data.totalResults);
            console.log(`📊 Total available results: ${response.data.totalResults.toLocaleString()}`);
          }
          
          setBroadeningOptions([]);
        } else {
          // =================================================================
          // STANDARD SEARCH - ZoomInfo via PillBuilder / AI interpretation
          // =================================================================
          const hasPills = pillFilters && pillFilters.length > 0 && pillFilters.some(f => f.pills?.length > 0);
          
          if (hasPills) {
            console.log('🎯 Using pill-based search (pills exist)');
            response = await api.post('/api/ai/execute-pill-search', {
              filters: pillFilters,
              outputFields: pillOutputFields || outputFields,
              mode: apiMode,
              rpp: 100,
              rankingWeights: rankingWeights,
              query: searchQuery || undefined,
            });
            
            if (Array.isArray(response.data?.results)) {
              results = response.data.results;
            }
            
            if (response.data?.totalResults) {
              setTotalResultsCount(response.data.totalResults);
              console.log(`📊 Total available results: ${response.data.totalResults.toLocaleString()}`);
            }
          } else {
            // Pills-first: interpret query into pills on-the-fly, then search with them
            console.log('🔮 No pills yet — interpreting query to pills before search');
            try {
              const pillResponse = await api.post('/api/ai/interpret-to-pills', {
                query: searchQuery,
                mode: apiMode,
                location: location || undefined,
              });
              
              if (pillResponse.data?.success && pillResponse.data?.filters?.length > 0) {
                const freshFilters = pillResponse.data.filters;
                const freshOutputFields = pillResponse.data.outputFields || [];
                
                setPillFilters(freshFilters);
                setPillOutputFields(freshOutputFields);
                
                console.log('🎯 Pills interpreted — executing pill-based search');
                response = await api.post('/api/ai/execute-pill-search', {
                  filters: freshFilters,
                  outputFields: freshOutputFields.length > 0 ? freshOutputFields : outputFields,
                  mode: apiMode,
                  rpp: 100,
                  rankingWeights: rankingWeights,
                  query: searchQuery || undefined,
                });
                
                if (Array.isArray(response.data?.results)) {
                  results = response.data.results;
                }
                
                if (response.data?.totalResults) {
                  setTotalResultsCount(response.data.totalResults);
                  console.log(`📊 Total available results: ${response.data.totalResults.toLocaleString()}`);
                }
              } else {
                // Fallback: pill interpretation returned nothing, use legacy search
                console.log('⚠️ Pill interpretation empty — falling back to AI search');
                response = await api.post('/api/ai/ai-people-search', {
                  query: searchQuery,
                  rpp: 100,
                  searchMode: apiMode,
                  location: location || null,
                  remote: includeRemote,
                  outputFields: outputFields,
                  rankingWeights: rankingWeights,
                });
                
                if (response.data?.results?.people) {
                  results = response.data.results.people;
                } else if (Array.isArray(response.data?.contacts)) {
                  results = response.data.contacts;
                } else if (Array.isArray(response.data?.results)) {
                  results = response.data.results;
                }
                
                const total = response.data?.results?.totalResults 
                  || response.data?.totalResults 
                  || null;
                if (total && total > 0) {
                  setTotalResultsCount(total);
                }
              }
            } catch (pillErr) {
              console.error('⚠️ Pill interpretation failed, falling back to AI search:', pillErr.message);
              response = await api.post('/api/ai/ai-people-search', {
                query: searchQuery,
                rpp: 100,
                searchMode: apiMode,
                location: location || null,
                remote: includeRemote,
                outputFields: outputFields,
                rankingWeights: rankingWeights,
              });
              
              if (response.data?.results?.people) {
                results = response.data.results.people;
              } else if (Array.isArray(response.data?.contacts)) {
                results = response.data.contacts;
              } else if (Array.isArray(response.data?.results)) {
                results = response.data.results;
              }
              
              const total = response.data?.results?.totalResults 
                || response.data?.totalResults 
                || null;
              if (total && total > 0) {
                setTotalResultsCount(total);
              }
            }
          }
        }
      }

      // Store the saved search ID if created
      if (response.data?.savedSearchId) {
        setSavedSearchId(response.data.savedSearchId);
      }

      // ZIA: Don't expose search response details in console (IP protection)
      // Results extracted successfully
      if (results.length > 0) {
        setSearchResults(results);
        // Always show list view first - no auto-selection
        setSelectedLead(null);
        
        // Store search params for "Load More" pagination
        setLastSearchParams({
          searchType: effectiveSearchType,
          mode: effectiveMode,
          query: searchQuery,
          pillFilters: pillFilters?.length > 0 ? pillFilters : null,
          pillOutputFields: pillOutputFields?.length > 0 ? pillOutputFields : null,
          outputFields,
          location,
          includeRemote,
          precisionSearch,
          precisionRefinements: precisionSearch ? precisionRefinements : null,
          rankingWeights: getCurrentWeights(),
        });
        
        // Auto-open refine panel after successful search (DEX: immediate refinement access)
        // For People mode, the Refine panel now contains PrecisionPanel (ContactOut params)
        setShouldOpenRefinePanel(true);
        
        // Track in search path for wayfinder breadcrumbs
        // Check if this came from quick search (has source info in router state)
        const isQuickSearch = routerLocation.state?.source === 'quick_search';
        if (!isQuickSearch) {
          // Manual search - add to path
          addStep({
            type: 'search',
            query: searchQuery,
            mode: effectiveMode,
            source: {
              type: 'manual',
              name: searchQuery.length > 30 ? searchQuery.slice(0, 27) + '...' : searchQuery,
            },
            results: results.length,
            savedSearchId: response.data?.savedSearchId || null,
          });
        }
        
        // Auto-save search to history if not already saved by backend
        // The backend AI routes should always return savedSearchId - this is a fallback
        const backendSavedId = response.data?.savedSearchId || response.data?.saved_search_id;
        
        if (backendSavedId) {
          // Backend already saved it - use that ID
          setSavedSearchId(backendSavedId);
          console.log('✅ Search saved by backend with ID:', backendSavedId);
          toast.saved('Alert saved', 'Your search has been added to My Alerts.');
          // Notify GlobalHeader to refresh agent count
          window.dispatchEvent(new CustomEvent('eq:agent-updated'));
        } else {
          // Fallback: Frontend saves if backend didn't (rare case)
          console.log('⚠️ Backend did not return savedSearchId, attempting frontend save...');
          try {
            const saveResponse = await api.post('/api/saved-searches', {
              searchCriteria: { 
                query: searchQuery,
                mode: effectiveMode,
                location: location || null,
                pillFilters: pillFilters.length > 0 ? pillFilters : null,
              },
              leadType: effectiveSearchType === 'company' ? 'company' : 'contact',
              naturalQuery: searchQuery,
            });
            if (saveResponse.data?.savedSearch?.id) {
              setSavedSearchId(saveResponse.data.savedSearch.id);
              console.log('✅ Search saved by frontend with ID:', saveResponse.data.savedSearch.id);
              toast.saved('Alert saved', 'Your search has been added to My Alerts.');
              // Notify GlobalHeader to refresh agent count
              window.dispatchEvent(new CustomEvent('eq:agent-updated'));
            } else if (saveResponse.data?.savedSearchId) {
              setSavedSearchId(saveResponse.data.savedSearchId);
              console.log('✅ Search saved by frontend with ID:', saveResponse.data.savedSearchId);
              toast.saved('Alert saved', 'Your search has been added to My Alerts.');
              // Notify GlobalHeader to refresh agent count
              window.dispatchEvent(new CustomEvent('eq:agent-updated'));
            }
          } catch (saveErr) {
            console.warn('❌ Could not auto-save search:', saveErr);
            // Don't show error toast for duplicates - that's expected behavior
            if (saveErr.response?.status !== 409) {
              toast.info('Search not saved', 'Results are shown but not saved to My Alerts.');
            }
          }
        }
        
        // Refresh feeds to show the new/updated search in the sidebar
        fetchFeeds();
      } else {
        // Zero-results fallback: broaden by keeping only job title + industry pills
        const currentFilters = pillFilters?.length > 0 ? pillFilters : [];
        const hasPillsToRelax = currentFilters.some(f => f.pills?.length > 0);
        
        if (hasPillsToRelax && (effectiveSearchType === 'people' || effectiveSearchType === 'contact')) {
          console.log('⚠️ Zero results — attempting broadened search');
          try {
            const broadenedFilters = currentFilters
              .filter(f => ['jobTitle', 'industryCodes', 'jobFunction'].includes(f.id))
              .map(f => ({
                ...f,
                pills: f.pills.map(p => ({ ...p, status: p.status === 'excluded' ? 'excluded' : 'included' })),
              }));
            
            if (broadenedFilters.some(f => f.pills?.length > 0)) {
              const fallbackResponse = await api.post('/api/ai/execute-pill-search', {
                filters: broadenedFilters,
                outputFields: pillOutputFields || outputFields,
                mode: effectiveMode === 'company' ? 'people' : effectiveMode,
                rpp: 100,
                rankingWeights: rankingWeights,
                query: searchQuery || undefined,
              });
              
              const fallbackResults = Array.isArray(fallbackResponse.data?.results) 
                ? fallbackResponse.data.results : [];
              
              if (fallbackResults.length > 0) {
                setSearchResults(fallbackResults);
                setSelectedLead(null);
                if (fallbackResponse.data?.totalResults) {
                  setTotalResultsCount(fallbackResponse.data.totalResults);
                }
                setError('Your exact criteria returned no results. Showing broadened results — use the filters above to narrow down.');
                setLastSearchParams({
                  searchType: effectiveSearchType,
                  mode: effectiveMode,
                  query: searchQuery,
                  pillFilters: broadenedFilters,
                  pillOutputFields: pillOutputFields?.length > 0 ? pillOutputFields : null,
                  outputFields,
                  location,
                  includeRemote,
                  precisionSearch,
                  precisionRefinements: null,
                  rankingWeights: getCurrentWeights(),
                });
                setShouldOpenRefinePanel(true);
              } else {
                setError('No results found. Try adjusting your search filters.');
              }
            } else {
              setError('No results found. Try a different search.');
            }
          } catch (broadenErr) {
            console.error('Broadened search failed:', broadenErr.message);
            setError('No results found. Try a different search.');
          }
        } else {
          setError('No results found. Try a different search.');
        }
      }
    } catch (err) {
      console.error('Search failed:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        url: err.config?.url,
        message: err.message,
      });
      
      // Provide more specific error messages
      let errorMessage = 'Search failed. Please try again.';
      if (err.response?.status === 404) {
        errorMessage = `API endpoint not found (${err.config?.url}). Please refresh the page.`;
      } else if (err.response?.status === 500) {
        errorMessage = err.response?.data?.error || 'Server error. The search service may be unavailable.';
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }
      
      setError(errorMessage);
    } finally {
      // Clear progress animation
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setSearchProgress({ step: 0, message: '' });
      setIsSearching(false);
    }
  };
  
  // Update ref so quick search handler can call handleSearch
  handleSearchRef.current = handleSearch;

  // Handle enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Clear search results
  const handleNewSearch = () => {
    setSearchResults([]);
    setSelectedLead(null);
    setHasSearched(false);
    setSavedSearchId(null);
    setQuery('');
    setCurrentPage(1);
    setLastSearchParams(null);
    inputRef.current?.focus();
  };

  // Load more results (pagination)
  const handleLoadMore = async () => {
    if (!lastSearchParams || isLoadingMore) return;
    
    const nextPage = currentPage + 1;
    setIsLoadingMore(true);
    
    try {
      let response;
      let newResults = [];
      const { searchType, mode, query: searchQuery, pillFilters, pillOutputFields, outputFields, location, includeRemote, precisionSearch: lastPrecisionSearch, rankingWeights } = lastSearchParams;
      const loadMoreApiMode = mode === 'company' ? 'people' : mode;
      
      if (searchType === 'people' || searchType === 'contact') {
        if (lastPrecisionSearch && pillFilters?.length > 0) {
          // Pills-first pagination — reuse the merged pill filters from initial search
          response = await api.post('/api/ai/execute-pill-search', {
            filters: pillFilters,
            outputFields: pillOutputFields || outputFields,
            mode: loadMoreApiMode,
            rpp: 100,
            page: nextPage,
            rankingWeights: rankingWeights,
            query: searchQuery || undefined,
          });
          
          if (Array.isArray(response.data?.results)) {
            newResults = response.data.results;
          }
        } else if (lastPrecisionSearch) {
          // Fallback: no pills stored, use legacy precision-search
          response = await api.post('/api/ai/precision-search', {
            query: searchQuery,
            refinements: lastSearchParams.precisionRefinements || {},
            mode: mode,
            location: location || null,
            rpp: 100,
            page: nextPage,
            rankingWeights: rankingWeights,
          });
          
          if (Array.isArray(response.data?.results)) {
            newResults = response.data.results;
          }
        } else {
          // Standard search pagination
          const hasPills = pillFilters && pillFilters.length > 0 && pillFilters.some(f => f.pills?.length > 0);
          
          if (hasPills) {
            response = await api.post('/api/ai/execute-pill-search', {
              filters: pillFilters,
              outputFields: pillOutputFields || outputFields,
              mode: loadMoreApiMode,
              rpp: 100,
              page: nextPage,
              rankingWeights: rankingWeights,
              query: searchQuery || undefined,
            });
            
            if (Array.isArray(response.data?.results)) {
              newResults = response.data.results;
            }
          } else {
            response = await api.post('/api/ai/ai-people-search', {
              query: searchQuery,
              rpp: 100,
              page: nextPage,
              searchMode: loadMoreApiMode,
              location: location || null,
              remote: includeRemote,
              outputFields: outputFields,
              rankingWeights: rankingWeights,
            });
            
            if (response.data?.results?.people) {
              newResults = response.data.results.people;
            } else if (Array.isArray(response.data?.results)) {
              newResults = response.data.results;
            }
          }
        }
      } else if (searchType === 'company') {
        response = await api.post('/api/ai/ai-company-search-simple', {
          query: searchQuery,
          rpp: 100,
          page: nextPage,
          outputFields: outputFields,
        });
        
        if (Array.isArray(response.data?.companies)) {
          newResults = response.data.companies.map((company, idx) => ({
            id: company.id || `company_${idx}`,
            type: 'company',
            data: company,
          }));
        }
      }
      
      if (newResults.length > 0) {
        setSearchResults(prev => [...prev, ...newResults]);
        setCurrentPage(nextPage);
        console.log(`📄 Loaded page ${nextPage}: ${newResults.length} more results`);
      }
    } catch (err) {
      console.error('Failed to load more results:', err);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Enrich all signal results with contacts (for Demand mode)
  const handleEnrichAllContacts = async () => {
    // Get all signal results that need enrichment
    const signalResults = searchResults.filter(r => r.type === 'signal');
    if (signalResults.length === 0) return;
    
    setIsEnrichingAll(true);
    
    try {
      // Get company names from signal results
      const companyNames = signalResults
        .map(r => r.data?.companyName || r.data?.name)
        .filter(Boolean);
      
      // Call bulk enrichment endpoint
      const response = await api.post('/api/research/enrich-contacts', {
        companyNames: companyNames,
        industry: signalResults[0]?.data?.industry,
        query: query,
        rpp: 3, // 3 contacts per company for bulk
      });
      
      const enriched = response.data?.enrichedResults || [];
      
      // Update search results to include contacts inline
      const updatedResults = [...searchResults];
      
      // Add enriched contacts after their respective signal cards
      const newResults = [];
      for (const result of updatedResults) {
        newResults.push(result);
        
        if (result.type === 'signal') {
          const companyName = result.data?.companyName || result.data?.name;
          const enrichedCompany = enriched.find(
            e => e.companyName?.toLowerCase() === companyName?.toLowerCase()
          );
          
          if (enrichedCompany?.contacts?.length > 0) {
            // Mark the signal as enriched
            result.data.contactsLoaded = true;
            result.data.contacts = enrichedCompany.contacts;
            
            // Add contacts to results list
            for (const contact of enrichedCompany.contacts) {
              newResults.push(contact);
            }
          }
        }
      }
      
      setSearchResults(newResults);
      
    } catch (err) {
      console.error('Failed to enrich all contacts:', err);
    } finally {
      setIsEnrichingAll(false);
    }
  };

  // Save search as a feed
  const handleSaveAsFeed = async () => {
    if (savedSearchId) {
      navigate(`/app/feed/${savedSearchId}`);
    }
  };

  // Navigate to existing feed with loading state
  const handleSelectFeed = async (feed) => {
    setLoadingAgentId(feed.id);
    try {
      // Pre-fetch to verify the feed exists and has data
      const response = await api.get(`/api/saved-searches/${feed.id}`);
      if (response.data?.success) {
        navigate(`/app/feed/${feed.id}`);
      } else {
        toast.error('Could not load agent', 'This agent may have been deleted or is unavailable.');
      }
    } catch (err) {
      console.error('Failed to load agent:', err);
      toast.error('Failed to load agent', 'Please try again or refresh the page.', {
        onRetry: () => handleSelectFeed(feed)
      });
    } finally {
      setLoadingAgentId(null);
    }
  };

  // Results view - when we have search results
  // For signals/demand search, we don't need the full LeadDetailPanel
  const isSignalsSearch = searchMode === 'demand' || detectedSearchType === 'demand';
  
  // Extract checked/liked profiles for learning points (ZIA)
  const checkedProfiles = React.useMemo(() => {
    return displayResults.filter(lead => {
      const status = leadStatuses[lead.id || lead.personId];
      return status === 'liked';
    });
  }, [displayResults, leadStatuses]);

  // Apply refinement - update results with new query/filters
  const handleStreamingRefinement = async ({
    query: refineQuery,
    mode: refineMode,
    filters = [],
    outputFields = [],
    weights,
    listSize,
    listMode: refineListMode,
  } = {}) => {
    const nextQuery = (refineQuery || refineQueryText || query || '').trim();
    if (!nextQuery) return;
    
    console.log('🔄 FeedHome: Streaming refinement started', {
      query: nextQuery,
      mode: refineMode,
      filtersCount: filters?.length || 0,
      filters: filters?.map(f => ({
        id: f.id,
        label: f.label,
        included: f.pills?.filter(p => p.status === 'included').map(p => p.label || p.apiValue),
      })),
      weights,
      listSize,
    });
    
    const resolvedMode = refineMode || searchMode;
    const pillFilters = Array.isArray(filters) ? filters.filter(f => Array.isArray(f.pills)) : [];
    const hasPillFilters = pillFilters.length > 0;
    const shouldUsePillSearch = resolvedMode === 'jobs' || resolvedMode === 'demand' || hasPillFilters;
    
    if (!shouldUsePillSearch) {
      setRefineQueryText(nextQuery);
      await handleSearch(nextQuery, resolvedMode);
      return;
    }
    
    // When in People mode, use pills-first refinement path
    if (precisionSearch && resolvedMode === 'people') {
      setIsSearching(true);
      try {
        // Use stored pill filters if available, otherwise re-interpret
        let refineFilters = pillFilters?.length > 0 ? pillFilters : null;
        let refineOutputFields = pillOutputFields?.length > 0 ? pillOutputFields : [];
        
        if (!refineFilters) {
          const pillResp = await api.post('/api/ai/interpret-to-pills', {
            query: nextQuery,
            mode: resolvedMode,
            location: location || undefined,
          });
          refineFilters = pillResp.data?.filters || [];
          refineOutputFields = pillResp.data?.outputFields || [];
        }
        
        const response = await api.post('/api/ai/execute-pill-search', {
          filters: refineFilters,
          outputFields: refineOutputFields,
          mode: resolvedMode,
          rpp: listSize || 100,
          rankingWeights: weights || undefined,
          query: nextQuery || undefined,
        });
        
        const newResults = Array.isArray(response.data?.results) ? response.data.results : [];
        if (response.data?.totalResults) {
          setTotalResultsCount(response.data.totalResults);
        }
        
        if (newResults.length > 0) {
          setSearchResults(newResults);
          setQuery(nextQuery);
          setRefineQueryText(nextQuery);
          setHasSearched(true);
          setPillFilters(refineFilters);
          setPillOutputFields(refineOutputFields);
        }
      } catch (err) {
        console.error('Pills-first refinement failed:', err);
      } finally {
        setIsSearching(false);
      }
      return;
    }
    
    setIsSearching(true);
    try {
      const rpp = listSize || 25;
      let response;
      let newResults = [];
      
      if (resolvedMode === 'jobs' || resolvedMode === 'demand') {
        const fallbackFilters = hasPillFilters ? pillFilters : [{
          id: 'keywords',
          apiField: 'q',
          pills: [{ id: 'kw-0', label: nextQuery, status: 'included', apiValue: nextQuery }],
        }];
        
        response = await api.post('/api/ai/execute-pill-search', {
          filters: fallbackFilters,
          outputFields,
          mode: resolvedMode,
          rpp,
          savedSearchId: savedSearchId || undefined,
          rankingWeights: weights || undefined,
          replaceResults: !!savedSearchId,
        });
        
        if (resolvedMode === 'jobs') {
          if (response.data?.success && Array.isArray(response.data?.results)) {
            newResults = response.data.results.map((job, idx) => ({
              id: job.job_id || `job_${idx}`,
              type: 'job',
              data: {
                jobTitle: job.title,
                companyName: job.company_name || job.company,
                location: job.location,
                description: job.description || job.snippet,
                salary: job.salary || job.detected_extensions?.salary,
                postedDate: job.detected_extensions?.posted_at || job.posted,
                sourceUrl: job.apply_options?.[0]?.link || job.link || job.job_link,
                applyUrl: job.apply_options?.[0]?.link,
                sourcePlatform: job.via || 'Web',
                remoteType: job.detected_extensions?.work_from_home ? 'Remote' : 'On-site',
                employmentType: job.detected_extensions?.schedule_type,
                thumbnail: job.thumbnail,
                extensions: job.extensions || [],
              }
            }));
          }
        } else if (resolvedMode === 'demand') {
          if (response.data?.success && Array.isArray(response.data?.results)) {
            newResults = response.data.results.map((article, idx) => ({
              id: article.id || `signal_${idx}`,
              type: 'signal',
              data: {
                signalTitle: article.title,
                signalSnippet: article.snippet || article.description,
                signalDate: article.date || article.published_date,
                signalSource: typeof article.source === 'string' ? article.source 
                  : article.source?.name || article.source?.title || '',
                sourceUrl: article.link || article.url,
                sourceLogo: article.source?.icon || article.thumbnail,
                companyName: typeof article.source === 'string' ? article.source
                  : article.source?.name || article.title?.split(/[:\\-–—]/)[0]?.trim() || '',
                signalType: 'news',
                thumbnail: article.thumbnail,
                highlight: article.highlight,
              }
            }));
          }
        }
      } else if (resolvedMode === 'company') {
        if (!hasPillFilters) {
          await handleSearch(nextQuery, resolvedMode);
          return;
        }
        response = await api.post('/api/ai/execute-pill-search', {
          filters: pillFilters,
          outputFields,
          mode: 'company',
          rpp,
          savedSearchId: savedSearchId || undefined,
          rankingWeights: weights || undefined,
          replaceResults: !!savedSearchId,
        });
        
        const companies = response.data?.results || [];
        if (Array.isArray(companies) && companies.length > 0) {
          newResults = companies.map((company, idx) => ({
            id: company.id || `company_${idx}`,
            type: 'company',
            data: {
              companyName: company.name || company.companyName,
              industry: Array.isArray(company.primaryIndustry) ? company.primaryIndustry.join(', ') : (company.primaryIndustry || company.industry || ''),
              website: company.website,
              location: company.city ? `${company.city}${company.state ? `, ${company.state}` : ''}${company.country ? `, ${company.country}` : ''}` : (company.location || ''),
              city: company.city,
              state: company.state,
              country: company.country,
              employeeCount: company.employeeCount || company.employees,
              revenue: company.revenue || company.annualRevenue,
              description: company.description || company.shortDescription || '',
              logoUrl: company.logoUrl || company.logo || company.companyLogo,
              yearFounded: company.yearFounded,
              phone: company.phone,
              linkedInUrl: company.linkedInUrl || company.linkedinUrl,
              twitterUrl: company.twitterUrl,
              facebookUrl: company.facebookUrl,
              ticker: company.ticker,
              fundingAmount: company.fundingAmount,
              latestFundingRound: company.latestFundingRound,
              naicsCode: company.naicsCode,
              sicCode: company.sicCode,
              type: company.type,
            }
          }));
        }
      } else {
        if (!hasPillFilters) {
          await handleSearch(nextQuery, resolvedMode);
          return;
        }
        response = await api.post('/api/ai/execute-pill-search', {
          filters: pillFilters,
          outputFields,
          mode: resolvedMode,
          rpp,
          savedSearchId: savedSearchId || undefined,
          rankingWeights: weights || undefined,
          replaceResults: !!savedSearchId,
        });
        
        if (Array.isArray(response.data?.results)) {
          newResults = response.data.results;
        }
      }
      
      if (!newResults.length && Array.isArray(response?.data?.results)) {
        newResults = response.data.results;
      }
      
      // Capture total results count from refinement response
      if (response?.data?.totalResults) {
        setTotalResultsCount(response.data.totalResults);
      }
      
      if (savedSearchId) {
        try {
          await api.patch(`/api/ai/saved-searches/${savedSearchId}`, {
            naturalQuery: nextQuery,
            searchCriteria: {
              mode: resolvedMode,
              filters: pillFilters,
              outputFields,
              rankingWeights: weights,
              listSize: rpp,
              listMode: refineListMode || listMode,
            },
          });
        } catch (patchError) {
          console.warn('Refine save metadata failed:', patchError?.message || patchError);
        }
      } else if (response?.data?.savedSearchId) {
        setSavedSearchId(response.data.savedSearchId);
      }
      
      console.log('✅ FeedHome: Refinement complete', {
        resultsCount: newResults.length,
        filters: pillFilters,
        weights,
      });
      
      setSearchResults(newResults);
      setQuery(nextQuery);
      setRefineQueryText(nextQuery);
      setPillFilters(pillFilters);
      setPillOutputFields(outputFields);
      setRefinePillFilters(pillFilters);
      setRefinePillOutputFields(outputFields);
      setListSize(rpp);
      setListMode(refineListMode || listMode);
      setHasSearched(true);
      
      const initialCount = Math.min(rpp, newResults.length);
      setLoadedCount(initialCount);
      setHasMore(newResults.length > initialCount);
    } catch (error) {
      console.error('❌ Refinement error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  if (hasSearched && searchResults.length > 0) {
    return (
      <SearchProvider>
        <DebugErrorBoundary name="FeedHome:results">
        <SplitPaneLayout
          leftPanel={
            <RefineCoPilot
              query={refineQueryText || query}
              mode={searchMode}
              filters={refinePillFilters.length > 0 ? refinePillFilters : pillFilters}
              outputFields={refinePillOutputFields.length > 0 ? refinePillOutputFields : pillOutputFields}
              results={filteredResults}
              checkedProfiles={checkedProfiles}
              onStreamingUpdate={handleStreamingRefinement}
              onSaveAsNew={handleSaveAsNew}
              precisionRefinements={precisionRefinements}
              onPrecisionRefinementsChange={setPrecisionRefinements}
            />
          }
        >
          <div className="h-full w-full bg-[#f8f8f6] flex flex-col overflow-hidden">
        {/* Refine Panel Opener - Opens panel when triggered from callbacks */}
        <RefinePanelOpener 
          shouldOpen={shouldOpenRefinePanel}
          onOpened={() => setShouldOpenRefinePanel(false)}
        />
        
        {/* Agents Slide-out Panel - Unified for mobile and desktop */}
        {showMobileSidebar && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setShowMobileSidebar(false)}
            />
            {/* Panel */}
            <div className="fixed top-0 left-0 h-full w-full max-w-md bg-white shadow-2xl z-50 flex flex-col animate-in slide-in-from-left duration-300">
              <div className="flex items-center justify-between px-4 py-3 border-b border-black/5 safe-area-top">
                <button
                  onClick={() => setShowMobileSidebar(false)}
                  className="flex items-center gap-1.5 text-[var(--eq-purple-text)] text-sm font-medium"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Back
                </button>
                <h2 className="text-lg font-bold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                  My Alerts
                </h2>
                <div className="w-16" /> {/* Spacer for centering */}
              </div>
              <div className="flex-1 overflow-y-auto">
                <AgentGrid
                  agents={feeds}
                  isLoading={isLoadingFeeds}
                  loadingAgentId={loadingAgentId}
                  onSelectAgent={(f) => {
                    handleSelectFeed(f);
                    setShowMobileSidebar(false);
                  }}
                  onNewAgent={() => {
                    handleNewSearch();
                    setShowMobileSidebar(false);
                  }}
                  onDeleteAgent={async (id) => {
                    await api.delete(`/api/saved-searches/${id}`);
                    fetchFeeds();
                  }}
                  onShareAgent={async (id) => {
                    try {
                      const response = await api.post(`/api/saved-searches/${id}/share-link`);
                      const link = response.data?.shareUrl || `${window.location.origin}/share/feed/${id}`;
                      setShareLink(link);
                      setShowShareModal(true);
                    } catch (err) {
                      setShareLink(`${window.location.origin}/share/feed/${id}`);
                      setShowShareModal(true);
                    }
                  }}
                  onRenameAgent={async (id, newName) => {
                    await api.patch(`/api/saved-searches/${id}/name`, { name: newName });
                    fetchFeeds();
                  }}
                  hideHeader={true}
                  compact={true}
                />
              </div>
            </div>
          </>
        )}

        {/* Results Area */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Search Header with Breadcrumbs */}
          <header className="bg-white border-b border-black/5 safe-area-top">
            {/* Wayfinder Breadcrumbs - Shows search path for rabbit hole navigation */}
            {searchPath.length > 0 && (
              <div className="px-3 md:px-4 pt-2">
                <SearchBreadcrumbs />
              </div>
            )}
            
            <div className="flex items-center gap-2 md:gap-3 px-3 md:px-4 py-3">
              {/* ALWAYS visible Home button - users should NEVER feel stuck */}
              <button
                onClick={handleNewSearch}
                className="flex items-center justify-center w-9 h-9 bg-[var(--eq-purple)]/10 hover:bg-[var(--eq-purple)]/20 rounded-lg transition-colors flex-shrink-0 group"
                title="New Search (ESC)"
              >
                <Home className="w-4 h-4 text-[var(--eq-purple-text)] group-hover:scale-110 transition-transform" />
              </button>
              
              {/* Back button - shows when there's navigation history */}
              {canGoBack && (
                <button
                  onClick={goBack}
                  className="flex items-center gap-1 px-2 py-1.5 bg-black/5 hover:bg-[var(--eq-purple)]/10 rounded-lg transition-colors flex-shrink-0 group animate-in fade-in duration-200"
                  title="Go back to previous search"
                >
                  <ChevronLeft className="w-4 h-4 text-black/50 group-hover:text-[var(--eq-purple-text)] group-hover:-translate-x-0.5 transition-all" />
                  <span className="text-xs text-black/50 group-hover:text-[var(--eq-purple-text)] font-medium">Back</span>
                </button>
              )}
              
              {/* My Alerts button - ALWAYS visible for quick access */}
              <button
                onClick={() => isMobile ? setShowMobileSidebar(true) : setShowMobileSidebar(!showMobileSidebar)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 bg-[var(--eq-purple)]/10 hover:bg-[var(--eq-purple)]/20 rounded-lg transition-colors flex-shrink-0 group"
                title="My Alerts"
                data-tour="nav-agents"
              >
                <Layers className="w-4 h-4 text-[var(--eq-purple-text)]" />
                <span className="text-xs text-[var(--eq-purple-text)] font-medium">Alerts</span>
                {feeds.length > 0 && (
                  <span className="px-1.5 py-0.5 text-[10px] font-bold bg-[var(--eq-purple)] text-white rounded-full">
                    {feeds.length}
                  </span>
                )}
              </button>
              
              {!isMobile && <ChevronRight className="w-3 h-3 text-black/20 flex-shrink-0" />}
              
              {/* Search Type - hidden on mobile */}
              <span className="text-xs text-black/50 hidden md:inline">
                {AGENT_MODES.find(m => m.id === searchMode)?.name}
              </span>
              
              <ChevronRight className="w-3 h-3 text-black/20 flex-shrink-0 hidden md:inline" />
              
              {/* Current Search */}
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div className="min-w-0">
                  <h1 className="text-sm font-semibold text-black truncate max-w-[180px] md:max-w-none" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {query}
                  </h1>
                  {/* DEX: Simplified header - premium minimalism */}
                  <div className="flex items-center gap-2 text-xs text-black/40">
                    <span>
                      {(() => {
                        const count = totalResultsCount && totalResultsCount > searchResults.length
                          ? `${searchResults.length.toLocaleString()} loaded of ${totalResultsCount.toLocaleString()} available`
                          : `${displayResults.length.toLocaleString()} contacts`;
                        if (searchMode === 'company' && displayResults.length > 0) {
                          const uniqueCompanies = new Set(displayResults.map(r => {
                            const d = r.data || r.lead_data || r;
                            return d.companyName || d.company_name || (typeof d.company === 'object' ? d.company?.name : d.company) || '';
                          }).filter(Boolean)).size;
                          return `${count} across ${uniqueCompanies} ${uniqueCompanies === 1 ? 'company' : 'companies'}`;
                        }
                        return count;
                      })()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Spacer to push filters to center */}
              <div className="flex-1" />

              {/* Status Filter Buttons - Centered */}
              <div className="flex items-center gap-1 px-3 py-1 bg-black/[0.02] rounded-full">
                <button
                  onClick={() => setStatusFilter('all')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    statusFilter === 'all' 
                      ? 'bg-white shadow-sm text-black' 
                      : 'text-black/40 hover:text-black/60'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setStatusFilter('liked')}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    statusFilter === 'liked' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'text-emerald-600/60 hover:text-emerald-600'
                  }`}
                >
                  <Check className="w-3 h-3" />
                  Liked
                </button>
                <button
                  onClick={() => setStatusFilter('archived')}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-all ${
                    statusFilter === 'archived' 
                      ? 'bg-black/5 text-black/70' 
                      : 'text-black/30 hover:text-black/50'
                  }`}
                >
                  <X className="w-3 h-3" />
                  Skip
                </button>
              </div>

              {/* Spacer for symmetry */}
              <div className="flex-1" />

              {/* Compact Toolbar - Icon only with tooltips */}
              <div className="flex items-center gap-1">
                {/* View Toggle - Kanban commented out per user request */}
                {/* 
                <div className="flex items-center p-0.5 bg-black/5 rounded-lg">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
                    title="List View"
                  >
                    <LayoutList className={`w-4 h-4 ${viewMode === 'list' ? 'text-[var(--eq-purple-text)]' : 'text-black/40'}`} />
                  </button>
                  <button
                    onClick={() => setViewMode('kanban')}
                    className={`p-1.5 rounded transition-colors ${viewMode === 'kanban' ? 'bg-white shadow-sm' : 'hover:bg-white/50'}`}
                    title="Kanban View"
                  >
                    <Columns className={`w-4 h-4 ${viewMode === 'kanban' ? 'text-[var(--eq-purple-text)]' : 'text-black/40'}`} />
                  </button>
                </div>
                */}

                {/* Refine Results - Opens SplitPaneLayout panel */}
                <RefineButton
                  onClick={() => {
                    setRefineQueryText(query);
                    // Use current pill filters and output fields
                    setRefinePillFilters(pillFilters);
                    setRefinePillOutputFields(pillOutputFields);
                  }}
                />

                {/* Bulk Actions */}
                <button
                  onClick={() => setShowBulkActions(!showBulkActions)}
                  className={`p-2 rounded-lg transition-colors relative ${
                    showBulkActions || selectedLeads.length > 0
                      ? 'bg-[var(--eq-purple)] text-white'
                      : 'bg-black/5 text-black/60 hover:bg-black/10'
                  }`}
                  title="Bulk Actions"
                >
                  <Layers className="w-4 h-4" />
                  {selectedLeads.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                      {selectedLeads.length}
                    </span>
                  )}
                </button>

                {/* Export */}
                <button
                  onClick={handleExportCSV}
                  className="p-2 rounded-lg bg-black/5 text-black/60 hover:bg-black/10 transition-colors"
                  title="Export CSV"
                >
                  <Download className="w-4 h-4" />
                </button>

                {/* More Actions Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowMoreActions(!showMoreActions)}
                    className="p-2 rounded-lg bg-black/5 text-black/60 hover:bg-black/10 transition-colors"
                    title="More actions"
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  
                  {showMoreActions && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowMoreActions(false)} />
                      <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-xl border border-black/10 py-1 min-w-[200px]">
                        <button
                          onClick={() => { handleShareList(savedSearchId); setShowMoreActions(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 flex items-center gap-2"
                        >
                          <Share2 className="w-4 h-4" /> Share
                        </button>
                        <button
                          onClick={() => { handleDuplicateList(savedSearchId); setShowMoreActions(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 flex items-center gap-2"
                        >
                          <Copy className="w-4 h-4" /> Duplicate
                        </button>
                        {savedSearchId && (
                          <button
                            onClick={() => { handleSaveAsFeed(); setShowMoreActions(false); }}
                            className="w-full px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 flex items-center gap-2"
                          >
                            <BookmarkPlus className="w-4 h-4" /> Save
                          </button>
                        )}
                        <button
                          onClick={() => { setShouldOpenRefinePanel(true); setShowMoreActions(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 flex items-center gap-2"
                        >
                          <Columns className="w-4 h-4" /> Configure Fields
                        </button>
                        <button
                          onClick={() => { setShowDashboard(true); setShowMoreActions(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 flex items-center gap-2"
                        >
                          <BarChart3 className="w-4 h-4" /> Stats
                        </button>
                        <button
                          onClick={() => { setShowBrandVoice(true); setShowMoreActions(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-black/70 hover:bg-black/5 flex items-center gap-2"
                        >
                          <MessageSquare className="w-4 h-4" /> Brand Voice
                        </button>
                        <hr className="my-1 border-black/5" />
                        <button
                          onClick={() => { setShowIntegrations(true); setShowMoreActions(false); }}
                          className="w-full px-3 py-2 text-left text-sm text-[var(--eq-purple-text)] hover:bg-[var(--eq-purple)]/5 flex items-center gap-2"
                        >
                          <Zap className="w-4 h-4" /> Integrations
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Refresh */}
                <button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="p-2 hover:bg-black/5 rounded-lg transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 text-black/60 ${isSearching ? 'animate-spin' : ''}`} />
                </button>

                {/* User Profile Menu */}
                {isAuthenticated && (
                  <div className="relative ml-2">
                    <button
                      onClick={() => setShowUserMenu(!showUserMenu)}
                      className="flex items-center gap-2 p-1 rounded-lg hover:bg-black/5 transition-colors"
                      title={user?.name || 'Account'}
                    >
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-xs font-bold">
                        {getUserInitials()}
                      </div>
                    </button>

                    {/* Dropdown Menu */}
                    {showUserMenu && (
                      <>
                        {/* Backdrop to close menu */}
                        <div 
                          className="fixed inset-0 z-40" 
                          onClick={() => setShowUserMenu(false)} 
                        />
                        <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl shadow-xl border border-black/10 z-50 overflow-hidden">
                          {/* User Info Header */}
                          <div className="p-4 border-b border-black/5 bg-gradient-to-r from-[var(--eq-purple)]/5 to-[var(--eq-teal)]/5">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center text-white text-lg font-bold">
                                {getUserInitials()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-black truncate">{user?.name || 'User'}</p>
                                <p className="text-xs text-black/50 truncate">{user?.email}</p>
                              </div>
                            </div>
                            {/* Subscription Status */}
                            {subscription && (
                              <div className="mt-3 p-2 bg-white rounded-lg border border-[var(--eq-purple)]/10">
                                <div className="flex items-center gap-2 text-xs">
                                  <Zap className="w-3.5 h-3.5 text-[var(--eq-purple-text)]" />
                                  <span className="font-medium text-[var(--eq-purple-text)]">
                                    {subscription?.unlimited 
                                      ? 'Unlimited Plan' 
                                      : `${getUsedCredits()} / ${getDailyLimit()} searches used`}
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Menu Items */}
                          <div className="py-1">
                            <button
                              onClick={() => {                                navigate('/app/settings');
                                setShowUserMenu(false);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-black/70 hover:bg-black/5 transition-colors"
                            >
                              <Settings className="w-4 h-4" />
                              Settings
                            </button>
                            <button
                              onClick={() => {                                navigate('/app/settings?tab=workspace');
                                setShowUserMenu(false);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-black/70 hover:bg-black/5 transition-colors"
                            >
                              <DollarSign className="w-4 h-4" />
                              Billing & Plan
                            </button>
                          </div>
                          
                          {/* Logout - Hidden in production */}
                          {process.env.NODE_ENV !== 'production' && (
                            <div className="border-t border-black/5 py-1">
                              <button
                                onClick={() => {
                                  logout({ logoutParams: { returnTo: window.location.origin } });
                                  setShowUserMenu(false);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <LogOut className="w-4 h-4" />
                                Sign Out
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Filter Bar */}
          {showFilters && (
            <div className="bg-white border-b border-black/5 px-4 py-3">
              <div className="flex items-center gap-2 flex-wrap">
                {/* Search filter */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-lg flex-1 min-w-[200px] max-w-[300px]">
                  <Search className="w-3.5 h-3.5 text-black/40" />
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    placeholder="Search results..."
                    className="flex-1 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                  />
                </div>

                {/* Location filter */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-lg">
                  <MapPin className="w-3.5 h-3.5 text-black/40" />
                  <input
                    type="text"
                    value={filters.location}
                    onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="Location"
                    className="w-24 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                  />
                </div>

                {/* Company filter */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-lg">
                  <Building2 className="w-3.5 h-3.5 text-black/40" />
                  <input
                    type="text"
                    value={filters.company}
                    onChange={(e) => setFilters(prev => ({ ...prev, company: e.target.value }))}
                    placeholder="Company"
                    className="w-24 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                  />
                </div>

                {/* Title/Role filter */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-lg">
                  <Briefcase className="w-3.5 h-3.5 text-black/40" />
                  <input
                    type="text"
                    value={filters.title || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Title"
                    className="w-24 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                  />
                </div>

                {/* Industry filter */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/5 rounded-lg">
                  <TrendingUp className="w-3.5 h-3.5 text-black/40" />
                  <input
                    type="text"
                    value={filters.industry || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                    placeholder="Industry"
                    className="w-24 bg-transparent text-xs text-black placeholder-black/40 focus:outline-none"
                  />
                </div>

                {/* Clear filters */}
                {Object.values(filters).some(f => f) && (
                  <button
                    onClick={clearFilters}
                    className="px-2.5 py-1.5 text-xs text-[var(--eq-purple-text)] hover:bg-[var(--eq-purple)]/10 rounded-lg transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>

              {/* Custom Fields Display */}
              {customFields.length > 0 && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-black/5">
                  <span className="text-xs text-black/40">Custom:</span>
                  {customFields.map(field => (
                    <div key={field.id} className="flex items-center gap-1 px-2 py-1 bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] rounded text-xs">
                      <Wand2 className="w-3 h-3" />
                      <span>{field.label}</span>
                      <button
                        onClick={() => removeCustomField(field.id)}
                        className="ml-1 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bulk Actions Bar - Enhanced with clear labels */}
          {showBulkActions && (
            <div className="bg-gradient-to-r from-[var(--eq-purple)]/10 to-[var(--eq-teal)]/10 border-b border-[var(--eq-purple)]/20 px-5 py-3">
              <div className="flex items-center gap-4 flex-wrap">
                {/* Select All */}
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-white border-2 border-[var(--eq-purple)]/30 hover:border-[var(--eq-purple)] hover:bg-[var(--eq-purple)]/5 transition-all shadow-sm"
                >
                  <Check className={`w-4 h-4 ${selectedLeads.length === displayResults.length ? 'text-[var(--eq-purple)]' : 'text-black/40'}`} />
                  {selectedLeads.length === displayResults.length ? 'Deselect All' : 'Select All Leads'}
                </button>

                {/* Selection count badge */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--eq-purple)]/20 rounded-full">
                  <span className="text-sm font-bold text-[var(--eq-purple)]">
                    {selectedLeads.length}
                  </span>
                  <span className="text-sm text-[var(--eq-purple-text)]">
                    {selectedLeads.length === 1 ? 'lead' : 'leads'} selected
                  </span>
                </div>

                {selectedLeads.length > 0 && (
                  <div className="flex items-center gap-3 ml-auto">
                    {/* Bulk Status Change */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-black/50 uppercase">Status:</span>
                      <select
                        onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value)}
                        className="px-3 py-2 rounded-lg text-sm font-medium bg-white border-2 border-black/10 focus:outline-none focus:border-[var(--eq-purple)] cursor-pointer"
                        defaultValue=""
                      >
                        <option value="" disabled>Change Status</option>
                        {LEAD_STATUSES.map(status => (
                          <option key={status.id} value={status.id}>{status.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Divider */}
                    <div className="h-8 w-px bg-black/10" />

                    {/* Add to Reach — bulk add selected leads */}
                    <button
                      onClick={handleBulkAddToReach}
                      disabled={isBulkAddingToReach}
                      className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] text-white hover:opacity-90 transition-opacity disabled:opacity-50 shadow-lg"
                      title="Add selected leads to Reach"
                    >
                      {isBulkAddingToReach ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Add to Reach
                        </>
                      )}
                    </button>
                  </div>
                )}

                <button
                  onClick={() => { setShowBulkActions(false); setSelectedLeads([]); }}
                  className="ml-2 px-3 py-2 text-sm font-medium text-black/40 hover:text-black/70 hover:bg-black/5 rounded-lg transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Results Content */}
          <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
            {viewMode === 'list' ? (
              <>
                {/* Lead List - Always visible, drawer overlays on mobile */}
                <div className="flex-1 overflow-y-auto p-3 md:p-4 flex flex-col">
                  {/* Order Results Bar - Unified sorting & keyword prioritization (experimental) */}
                  {isScoutExperimental && displayResults.length > 0 && (
                    <OrderResultsBar
                      rankingPreset={rankingPreset}
                      onRankingChange={(presetId, weights) => {
                        setRankingPreset(presetId);
                        setRankingWeights(weights);
                      }}
                      roleKeywords={roleKeywords}
                      companyKeywords={companyKeywords}
                      onAddRoleKeyword={addRoleKeyword}
                      onRemoveRoleKeyword={removeRoleKeyword}
                      onAddCompanyKeyword={addCompanyKeyword}
                      onRemoveCompanyKeyword={removeCompanyKeyword}
                      onClearAllKeywords={clearAllKeywords}
                      roleMatchCount={roleMatchCount}
                      companyMatchCount={companyMatchCount}
                      keywordMatchCount={keywordMatchCount}
                      totalResults={totalResultsCount || displayResults.length}
                      loadedResults={displayResults.length}
                      className="max-w-3xl mx-auto w-full mb-4"
                    />
                  )}
                  
                  {/* Broadening suggestions when results are low */}
                  {broadeningOptions.length > 0 && displayResults.length > 0 && displayResults.length < 20 && (
                    <div className="max-w-3xl mx-auto w-full mb-3 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-xl p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-amber-600 text-sm">💡</span>
                        <span className="text-xs font-semibold text-amber-800/80">Fewer results than expected?</span>
                        <span className="text-[10px] text-amber-600/60">Consider broadening your search</span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {broadeningOptions.map((opt, idx) => (
                          <button
                            key={idx}
                            className="text-[11px] px-2.5 py-1 rounded-lg bg-white border border-amber-200/60 text-amber-800 hover:bg-amber-100/60 transition-colors shadow-sm"
                            title={`Estimated ~${opt.estimatedTotal?.toLocaleString()} total results`}
                            onClick={async () => {
                              console.log('Apply broadening:', opt);
                              try {
                                setIsSearching(true);
                                setBroadeningOptions([]);
                                const broadenedRefinements = { ...(precisionRefinements || {}), ...opt.criteria };
                                const response = await api.post('/api/ai/precision-search', {
                                  query: query,
                                  refinements: broadenedRefinements,
                                  mode: searchMode === 'company' ? 'company' : 'people',
                                  location: location || null,
                                  rpp: 100,
                                });
                                if (response.data?.results) {
                                  const mapped = response.data.results.map(r => ({
                                    ...r,
                                    data: r,
                                    lead_data: r,
                                    isPrecisionResult: true,
                                  }));
                                  setSearchResults(mapped);
                                  setTotalResultsCount(response.data.totalResults || mapped.length);
                                  if (response.data.broadeningOptions?.length > 0) {
                                    setBroadeningOptions(response.data.broadeningOptions);
                                  }
                                  setPrecisionRefinements(broadenedRefinements);
                                }
                              } catch (err) {
                                console.error('Broadening search failed:', err);
                              } finally {
                                setIsSearching(false);
                              }
                            }}
                          >
                            {opt.label}
                            {opt.estimatedAdditional > 0 && (
                              <span className="ml-1 text-amber-600/70 font-medium">
                                +{opt.estimatedAdditional.toLocaleString()}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 max-w-3xl mx-auto flex-1">
                    {displayResults.slice(0, loadedCount).map((lead, index) => {
                      const leadData = lead.data || lead.lead_data || lead;
                      const sourceUrl = leadData.sourceUrl || leadData.link || leadData.url || '';
                      // Ensure unique key: combine ID with index to prevent duplicate key warnings
                      const uniqueKey = `${lead.id || lead.personId || 'unknown'}-${index}`;
                      
                      return (
                        <LeadCard
                          key={uniqueKey}
                          lead={lead}
                          isSelected={!isSignalsSearch && selectedLead === lead}
                          onSelect={() => {
                            // For signals, open article directly. For others, select lead
                            if (isSignalsSearch) {
                              const leadData = lead.data || lead.lead_data || lead;
                              const articleUrl = leadData.sourceUrl || leadData.link || leadData.url || '';
                              if (articleUrl) {
                                window.open(articleUrl, '_blank', 'noopener,noreferrer');
                              } else {
                                // Fallback: show preview if no URL
                                setSignalPreview(lead);
                              }
                            } else {
                              setSelectedLead(lead);
                            }
                          }}
                          status={leadStatuses[lead.id || lead.personId] || 'inbox'}
                          onStatusChange={(status) => updateLeadStatus(lead.id || lead.personId, status)}
                          customFields={customFields}
                          isBulkMode={showBulkActions}
                          isBulkSelected={selectedLeads.includes(lead.id || lead.personId)}
                          onBulkToggle={() => {
                            const id = lead.id || lead.personId;
                            setSelectedLeads(prev => 
                              prev.includes(id) 
                                ? prev.filter(x => x !== id)
                                : [...prev, id]
                            );
                          }}
                          searchMode={searchMode}
                          onFindContacts={(company, contacts) => {
                            console.log('Found contacts at', company, contacts);
                          }}
                          onFindDecisionMaker={(company, dm) => {
                            console.log('Found decision maker at', company, dm);
                          }}
                          onRefineSearch={(context, data) => {
                            // Open refine panel with context from this result
                            setRefineQueryText(context);
                            setShouldOpenRefinePanel(true);
                            // Pre-fill the query with context
                            setQuery(context);
                          }}
                          onFindSimilar={(filters, keyElements) => {
                            // Open refine panel with pre-filled filters
                            setRefineQueryText(`${keyElements.jobTitle || ''} at ${keyElements.companyName || ''}`.trim() || 'Find similar');
                            setRefinePillFilters(filters);
                            setShouldOpenRefinePanel(true);
                          }}
                          cardIndex={index}
                          onNextLead={() => {
                            // Auto-scroll to next unprocessed lead after action
                            const currentIndex = displayResults.findIndex(l => 
                              (l.id || l.personId) === (lead.id || lead.personId)
                            );
                            // Find next lead that's still in inbox status
                            const nextLead = displayResults.slice(currentIndex + 1).find(l => {
                              const status = leadStatuses[l.id || l.personId] || 'inbox';
                              return status === 'inbox';
                            });
                            if (nextLead) {
                              // Scroll the next card into view smoothly
                              setTimeout(() => {
                                const nextElement = document.querySelector(`[data-lead-id="${nextLead.id || nextLead.personId}"]`);
                                if (nextElement) {
                                  nextElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                              }, 100);
                            }
                          }}
                          // Guest mode - blur cards after first, gate all clicks
                          isGuest={isGuest}
                          onGuestClick={() => requireAuth('view full profile')}
                          // Keyword match indicators from boost
                          keywordMatches={lead._keywordMatches}
                          hasKeywordMatch={lead._hasKeywordMatch}
                        />
                      );
                    })}
                  </div>
                  {/* Load More Footer - Client-side pagination */}
                  {displayResults.length > loadedCount && (
                    <LoadMoreFooter
                      currentCount={loadedCount}
                      totalAvailable={displayResults.length}
                      onLoadMore={(increment) => {
                        setLoadedCount(prev => Math.min(prev + increment, displayResults.length));
                        setHasMore(loadedCount + increment < displayResults.length);
                      }}
                      isLoading={false}
                    />
                  )}
                  
                  {/* Server-side Load More - Fetch additional results from ZoomInfo */}
                  {totalResultsCount && searchResults.length < totalResultsCount && lastSearchParams && (
                    <div className="flex justify-center py-6 border-t border-black/5 mt-4">
                      <button
                        onClick={handleLoadMore}
                        disabled={isLoadingMore}
                        className="flex items-center gap-2 px-6 py-3 bg-[#5a20ff]/10 hover:bg-[#5a20ff]/20 text-[#5a20ff] rounded-full font-medium transition-colors disabled:opacity-50"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading more...
                          </>
                        ) : (
                          <>
                            Load More ({(totalResultsCount - searchResults.length).toLocaleString()} remaining)
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Lead Detail Panel - Drawer on mobile, sidebar on desktop */}
                {selectedLead && !isSignalsSearch && (
                  <>
                    {/* Backdrop overlay for mobile drawer */}
                    {isMobile && (
                      <div 
                        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
                        onClick={() => setSelectedLead(null)}
                      />
                    )}
                    {/* Drawer panel */}
                    <div className={`${
                      isMobile 
                        ? 'fixed right-0 top-0 bottom-0 z-50 w-[90%] max-w-[500px] shadow-2xl animate-slide-left' 
                        : 'w-[500px] flex-shrink-0'
                    } bg-white`}>
                      <LeadDetailPanel 
                        lead={selectedLead} 
                        onClose={() => setSelectedLead(null)}
                        customFields={customFields}
                        fieldOrder={fieldOrder}
                        status={leadStatuses[selectedLead.id || selectedLead.personId] || 'inbox'}
                        onStatusChange={(status) => updateLeadStatus(selectedLead.id || selectedLead.personId, status)}
                        onSimilarSearch={handleSimilarSearch}
                        searchMode={searchMode}
                        isMobile={isMobile}
                      />
                    </div>
                  </>
                )}
              </>
            ) : (
              /* Kanban View */
              <KanbanView
                columns={kanbanColumns}
                statuses={LEAD_STATUSES}
                selectedLead={selectedLead}
                onSelectLead={setSelectedLead}
                onStatusChange={updateLeadStatus}
                customFields={customFields}
              />
            )}
          </div>
        </main>

        {/* Custom Field Modal */}
        {showCustomFieldModal && (
          <CustomFieldModal
            onClose={() => setShowCustomFieldModal(false)}
            onAdd={addCustomField}
          />
        )}

        {/* Share Modal */}
        {showShareModal && (
          <ShareModal
            shareLink={shareLink}
            onClose={() => setShowShareModal(false)}
            onCopy={copyShareLink}
          />
        )}

        {/* Signal Preview Modal */}
        {signalPreview && (
          <SignalPreviewModal
            signal={signalPreview}
            onClose={() => setSignalPreview(null)}
            onLike={() => {
              const id = signalPreview.id || signalPreview.personId;
              updateLeadStatus(id, 'liked');
              setSignalPreview(null);
            }}
            onArchive={() => {
              const id = signalPreview.id || signalPreview.personId;
              updateLeadStatus(id, 'archived');
              setSignalPreview(null);
            }}
            onSearch={(searchTerm) => {
              // Start a new search from the headline/company
              setQuery(searchTerm);
              setSignalPreview(null);
              // Trigger search after query is set
              setTimeout(() => handleSearch(), 100);
            }}
          />
        )}

        {/* Enrich Modal */}
        {showEnrichModal && (
          <EnrichModal
            onClose={() => setShowEnrichModal(false)}
            onEnrich={handleEnrichList}
            progress={enrichmentProgress}
          />
        )}

        {/* Refine Co-Pilot is now integrated via SplitPaneLayout */}
        
        {/* Dashboard Modal */}
        <Dashboard
          isOpen={showDashboard}
          onClose={() => setShowDashboard(false)}
          likedCount={processedCount.liked}
          archivedCount={processedCount.archived}
        />
        
        {/* Brand Voice Settings Modal */}
        <BrandVoiceSettings
          isOpen={showBrandVoice}
          onClose={() => setShowBrandVoice(false)}
        />
        
        {/* Watch Website Modal */}
        <WatchWebsiteModal
          isOpen={showWatchWebsiteModal}
          onClose={() => setShowWatchWebsiteModal(false)}
          onSuccess={handleWatchWebsiteSuccess}
        />
        
        {/* Keyboard Shortcuts Help Modal - Press ? to toggle */}
        <KeyboardShortcutsHelp
          isOpen={showKeyboardHelp}
          onClose={() => setShowKeyboardHelp(false)}
        />
        
        {/* Sign Up Gate Modal - Prompts guests to sign up */}
        <SignUpGateModal 
          isOpen={showGate} 
          onClose={closeGate} 
          feature={gateFeature}
        />
        
        {/* Bulk Generated Emails Modal — removed, replaced by bulk "Add to Reach" */}
        
        {/* New Leads Notification */}
        {newLeadsNotification && (
          <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 duration-300">
            <div className="bg-white rounded-2xl shadow-2xl border border-[#52dcb3]/20 p-4 flex items-center gap-4 max-w-sm">
              <div className="w-12 h-12 rounded-xl bg-[var(--eq-teal)] flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {newLeadsNotification.count} new leads found
                </p>
                <p className="text-xs text-black/50 truncate">
                  from {newLeadsNotification.feedName}
                </p>
              </div>
              <button
                onClick={() => {
                  if (newLeadsNotification.feedId) {
                    navigate(`/app/feed/${newLeadsNotification.feedId}`);
                  }
                  setNewLeadsNotification(null);
                }}
                className="px-3 py-1.5 bg-[var(--eq-purple)] text-white text-xs font-semibold rounded-full hover:bg-[var(--eq-purple)]/90 transition-colors"
              >
                View
              </button>
              <button
                onClick={() => setNewLeadsNotification(null)}
                className="p-1 text-black/30 hover:text-black/60"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
        
        {/* Celebration Toast - Every 10 leads! */}
        {celebration && (
          <div className="fixed inset-0 pointer-events-none z-[100] flex items-center justify-center">
            {/* Confetti-like particles */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-3 h-3 rounded-full animate-bounce"
                  style={{
                    left: `${10 + Math.random() * 80}%`,
                    top: `${20 + Math.random() * 40}%`,
                    backgroundColor: ['#5a20ff', '#52dcb3', '#f59e0b', '#ef4444', '#10b981'][i % 5],
                    animationDelay: `${i * 50}ms`,
                    animationDuration: `${600 + Math.random() * 400}ms`,
                    opacity: 0.8,
                  }}
                />
              ))}
            </div>
            
            {/* Main celebration card */}
            <div className="pointer-events-auto bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] rounded-3xl shadow-2xl p-6 text-center animate-bounce-once">
              <div className="text-5xl mb-2">
                {celebration.count >= 50 ? '🏆' : celebration.count >= 20 ? '🔥' : '🎉'}
              </div>
              <p className="text-white text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                {celebration.message}
              </p>
              <p className="text-white/70 text-sm mt-1">
                Keep going!
              </p>
            </div>
          </div>
        )}
          </div>
        </SplitPaneLayout>

        {/* Export Options Modal */}
        {showExportModal && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
            onClick={(e) => { if (e.target === e.currentTarget) setShowExportModal(false); }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in-0 zoom-in-95 duration-200">
              <div className="flex items-center justify-between p-5 border-b border-black/5">
                <div>
                  <h2 className="text-lg font-bold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Export {displayResults.length} Leads
                  </h2>
                  <p className="text-xs text-black/40 mt-0.5">18 standard fields always included</p>
                </div>
                <button
                  onClick={() => setShowExportModal(false)}
                  className="p-1.5 rounded-lg text-black/30 hover:text-black/60 hover:bg-black/5 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="p-5 space-y-3">
                <p className="text-xs font-medium text-black/50 uppercase tracking-wider">Include additional fields</p>
                {Object.entries(FIELD_GROUP_META).map(([key, meta]) => (
                  <label
                    key={key}
                    className={`flex items-start gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all ${
                      exportFieldGroups[key]
                        ? 'border-[var(--eq-purple)]/30 bg-[var(--eq-purple)]/[0.03]'
                        : 'border-black/5 hover:border-black/10'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={exportFieldGroups[key]}
                      onChange={(e) => setExportFieldGroups(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="mt-0.5 w-4 h-4 rounded accent-[var(--eq-purple)]"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-black">{meta.label}</span>
                      <p className="text-xs text-black/40 mt-0.5">{meta.description}</p>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-between p-5 border-t border-black/5 bg-black/[0.01]">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 text-sm text-black/50 hover:text-black/70 transition-colors"
                >
                  Cancel
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => doExportWithGroups([])}
                    disabled={isExporting}
                    className="px-4 py-2 text-sm font-medium text-black/70 bg-black/5 hover:bg-black/10 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Quick Export
                  </button>
                  <button
                    onClick={doExportWithGroups}
                    disabled={isExporting}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[var(--eq-purple)] hover:bg-[var(--eq-purple)]/90 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    {isExporting ? 'Exporting...' : 'Export CSV'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        </DebugErrorBoundary>
      </SearchProvider>
    );
  }

  // Handler for selecting agent from grid - with loading state
  const handleSelectAgentFromGrid = async (agent) => {
    setLoadingAgentId(agent.id);
    try {
      // Pre-fetch to verify the agent exists and has data
      const response = await api.get(`/api/saved-searches/${agent.id}`);
      if (response.data?.success) {
        navigate(`/app/feed/${agent.id}`);
      } else {
        toast.error('Could not load agent', 'This agent may have been deleted or is unavailable.');
      }
    } catch (err) {
      console.error('Failed to load agent:', err);
      toast.error('Failed to load agent', 'Please try again or refresh the page.', {
        onRetry: () => handleSelectAgentFromGrid(agent)
      });
    } finally {
      setLoadingAgentId(null);
    }
  };

  // Handler for creating new agent (opens search)
  const handleNewAgentFromGrid = () => {
    setIsSearchFocused(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Handler for deleting agent
  const handleDeleteAgentFromGrid = async (agentId) => {
    try {
      await api.delete(`/api/saved-searches/${agentId}`);
      fetchFeeds();
    } catch (err) {
      console.error('Failed to delete agent:', err);
    }
  };

  // Handler for sharing agent
  const handleShareAgentFromGrid = (agentId) => {
    handleShareList(agentId);
  };

  // Handler for renaming agent
  const handleRenameAgentFromGrid = async (agentId, newName) => {
    try {
      await api.put(`/api/saved-searches/${agentId}`, { name: newName });
      fetchFeeds();
    } catch (err) {
      console.error('Failed to rename agent:', err);
    }
  };

  // Default view - Dashboard (review-first, experimental only) or Search hero
  if (isScoutExperimental && isAuthenticated && homeView === 'dashboard') {
    return (
      <SearchProvider>
        <DebugErrorBoundary name="FeedHome:dashboard">
          <AgentDashboard
            onSwitchToSearch={() => {
              setHomeView('search');
              setTimeout(() => inputRef.current?.focus(), 100);
            }}
            onSelectAgent={(agent) => {
              navigate(`/app/feed/${agent.id}`);
            }}
          />
        </DebugErrorBoundary>
      </SearchProvider>
    );
  }

  // Search-first hero view (for guests or when user switches to search mode)
  return (
    <SearchProvider>
      <DebugErrorBoundary name="FeedHome:default">
      <div ref={containerRef} className="h-screen w-full bg-[#f8f8f6] flex flex-col overflow-hidden relative">

      {/* EQ inReach Promo Modal */}
      {showInReachPromo && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={dismissInReachPromo}>
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in slide-in-from-bottom duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative h-32 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center">
              <div className="text-center">
                <div className="text-white/80 text-sm font-medium tracking-wide mb-1">Introducing</div>
                <div className="text-white text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  EQ Enrich
                </div>
              </div>
              <button
                onClick={dismissInReachPromo}
                className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="p-6 text-center">
              <h3 className="text-lg font-semibold text-black mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                We've built something new
              </h3>
              <p className="text-sm text-black/60 mb-6 leading-relaxed">
                Our next-generation platform is here — faster search, smarter outreach, and a redesigned experience built for the way you work.
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="https://www.eqenrich.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={dismissInReachPromo}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white font-semibold rounded-xl hover:opacity-90 transition-opacity text-sm flex items-center justify-center gap-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Try EQ Enrich
                  <ExternalLink className="w-4 h-4" />
                </a>
                <button
                  onClick={dismissInReachPromo}
                  className="text-sm text-black/40 hover:text-black/60 transition-colors py-2"
                >
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EQ.app Animated Gradient Mesh Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Base gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#e8f5f0] via-[#f0f4ff] to-[#f5f0ff]" />
        {/* Teal glow - top left */}
        <div className="absolute -top-1/4 -left-1/4 w-[60%] h-[60%] bg-[#52dcb3]/30 rounded-full blur-[120px] animate-pulse" style={{ animationDuration: '8s' }} />
        {/* Purple glow - center right */}
        <div className="absolute top-1/4 -right-1/4 w-[50%] h-[70%] bg-[#5a20ff]/25 rounded-full blur-[100px] animate-pulse" style={{ animationDuration: '10s', animationDelay: '2s' }} />
        {/* Secondary teal - bottom */}
        <div className="absolute -bottom-1/4 left-1/4 w-[40%] h-[40%] bg-[#52dcb3]/20 rounded-full blur-[80px] animate-pulse" style={{ animationDuration: '12s', animationDelay: '4s' }} />
        {/* Grain overlay for premium feel */}
        <div 
          className="absolute inset-0 opacity-[0.03]" 
          style={{ 
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")` 
          }} 
        />
      </div>

      {/* Top Header Bar - Only for unauthenticated users */}
      {!isAuthenticated && (
        <header className="flex-shrink-0 pl-6 pr-4 sm:pr-6 py-3 bg-white/60 backdrop-blur-xl border-b border-black/5 flex items-center justify-between z-30 relative">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white border-2 border-[#5a20ff]/10 flex items-center justify-center shadow-sm">
              <img src="/favicon/favicon.svg" alt="EQ.app" className="w-6 h-6 object-contain" />
            </div>
            
            {/* Brand Name */}
            <h1 
              className="text-lg font-bold text-black hidden sm:block flex items-center gap-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Scout
              {isScoutExperimental && (
                <span className="text-xs font-medium text-[#5a20ff] bg-[#5a20ff]/10 px-2 py-0.5 rounded-full">
                  Experimental
                </span>
              )}
            </h1>
          </div>

          {/* Login/Signup for unauthenticated users */}
          <div className="flex items-center gap-2">
            <a
              href="https://www.eq.app"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-black/70 hover:text-black transition-colors"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              EQ.app
            </a>
            <button
              onClick={() => navigate('/pricing')}
              className="px-4 py-2 text-sm font-medium text-black/70 hover:text-black transition-colors"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Pricing
            </button>
            <button
              onClick={() => loginWithRedirect({ appState: { returnTo: '/app' } })}
              className="px-4 py-2 text-sm font-medium text-black/70 hover:text-black transition-colors"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Log in
            </button>
            <button
              onClick={() => loginWithRedirect({ appState: { returnTo: '/app' }, authorizationParams: { screen_hint: 'signup' } })}
              className="px-5 py-2 text-sm font-semibold text-white bg-[#5a20ff] rounded-full hover:bg-[#4a1ad6] transition-all duration-200 shadow-md shadow-[#5a20ff]/20 hover:shadow-lg hover:scale-[1.02]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Sign up free
            </button>
          </div>
        </header>
      )}

      {/* Main Content - Search Hero */}
      <main className="flex-1 flex flex-col items-center pr-4 sm:pr-8 overflow-y-auto relative z-10 pt-8 sm:pt-16" style={{ marginLeft: isMobile ? '0' : 'var(--sidebar-width, 64px)', marginTop: '56px', transition: 'margin-left 300ms ease-out' }}>
        <div className="w-full max-w-3xl py-8">
          {/* Back to Dashboard - For experimental mode only */}
          {isScoutExperimental && isAuthenticated && homeView === 'search' && (
            <div className="text-center mb-6">
              <button
                onClick={() => setHomeView('dashboard')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-black/50 hover:text-[#5a20ff] hover:bg-[#5a20ff]/5 rounded-lg transition-all"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <ChevronLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
            </div>
          )}
          
          {/* Hero Title - Elegant, minimal */}
          <div className="text-center mb-10">
            <h2 
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black leading-tight tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif", textWrap: 'balance' }}
            >
              What can I find for you?
            </h2>
          </div>

          {/* Main Search Input - Prominent, magnetic style */}
          <div className="space-y-6">
            {/* Search Bar Container - Premium glass effect */}
            <div 
              className={`relative bg-white/90 backdrop-blur-xl rounded-2xl border-2 transition-all duration-700 ease-out ${
                query 
                  ? 'border-[#5a20ff] shadow-[0_8px_40px_rgba(90,32,255,0.15)]' 
                  : 'border-black/10 hover:border-black/20 shadow-lg shadow-black/5 hover:shadow-xl'
              }`}
              data-tour="global-search-container"
            >
              {/* Input Row */}
              <div className="flex items-center gap-4 p-5 sm:p-6">
                <textarea
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    // Gate typing for unauthenticated users
                    if (!isAuthenticated && e.target.value.length > 0 && query.length === 0) {
                      requireAuth('search');
                      return;
                    }
                    setQuery(e.target.value);
                    // Auto-resize
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                  }}
                  onFocus={() => {
                    // Show gate when unauthenticated user focuses on search
                    if (!isAuthenticated && !query) {
                      // Don't block focus, but prepare to gate on first keystroke
                    }
                  }}
                  onKeyDown={(e) => {
                    // Gate search for unauthenticated users
                    if (!isAuthenticated && e.key !== 'Tab' && e.key !== 'Escape') {
                      e.preventDefault();
                      requireAuth('search');
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  placeholder={isAuthenticated 
                    ? (searchMode === 'demand' 
                        ? (SIGNAL_MODES.find(m => m.id === signalMode)?.placeholder || 'Series B fintech last 30 days')
                        : (AGENT_MODES.find(m => m.id === searchMode)?.placeholder || 'Java developers in Austin...'))
                    : 'Sign up to search for anyone...'
                  }
                  className="flex-1 bg-transparent text-black placeholder-black/30 focus:outline-none text-xl sm:text-2xl resize-none min-h-[32px] max-h-[120px] leading-relaxed"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  disabled={isSearching}
                  rows={1}
                  autoFocus={isAuthenticated}
                  data-tour="global-search-input"
                />
                
                {/* Primary Action Button - Always #5a20ff, magnetic effect */}
                <button
                  onClick={handleSearch}
                  disabled={(!query.trim() && !hasCompanyFilters) || isSearching}
                  className={`flex-shrink-0 flex items-center justify-center rounded-full transition-all duration-200 ${
                    (query.trim() || hasCompanyFilters) && !isSearching
                      ? 'w-14 h-14 bg-[#5a20ff] text-white hover:scale-105 active:scale-[0.98] shadow-lg shadow-[#5a20ff]/30'
                      : 'w-12 h-12 bg-black/5 text-black/30 cursor-not-allowed'
                  }`}
                  title="Search"
                  data-tour="global-search-submit"
                >
                  {isSearching ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <ArrowRight className="w-6 h-6" />
                  )}
                </button>
              </div>

              {/* Mode Pills - Below input, subtle */}
              <div className="flex items-center gap-2 px-5 sm:px-6 pb-3 pt-0 flex-wrap">
                {AGENT_MODES.map((mode) => {
                  const Icon = mode.icon;
                  const isActive = searchMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setSearchMode(mode.id)}
                      className={`group flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all duration-700 ${
                        isActive
                          ? 'bg-[#5a20ff] text-white'
                          : 'bg-black/5 text-black/50 hover:bg-black/10 hover:text-black'
                      }`}
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      <Icon className={`w-3.5 h-3.5 ${isActive ? '' : 'opacity-60 group-hover:opacity-100'}`} />
                      <span>{mode.name}</span>
                    </button>
                  );
                })}
                
                {/* Precision toggle removed — People mode is always ContactOut-powered */}
              </div>
              
              {/* Signal Sub-Modes - Show when Signals mode is active */}
              {searchMode === 'demand' && (
                <div className="px-5 sm:px-6 pb-5 sm:pb-6 animate-in fade-in slide-in-from-top-2 duration-500">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {SIGNAL_MODES.map((mode) => {
                      const Icon = mode.icon;
                      const isActive = signalMode === mode.id;
                      return (
                        <button
                          key={mode.id}
                          onClick={() => {
                            setSignalMode(mode.id);
                            // Update placeholder based on signal type
                            if (!query.trim()) {
                              inputRef.current?.setAttribute('placeholder', mode.placeholder);
                            }
                          }}
                          className={`group flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-all duration-700 ${
                            isActive
                              ? `bg-gradient-to-r ${mode.color} text-white shadow-sm`
                              : 'bg-black/[0.03] text-black/50 hover:bg-black/[0.06] hover:text-black/70'
                          }`}
                          title={mode.description}
                        >
                          <Icon className={`w-3 h-3 ${isActive ? '' : 'opacity-50 group-hover:opacity-70'}`} />
                          <span style={{ fontFamily: "'Playfair Display', serif" }}>{mode.name}</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* Signal mode description */}
                  <p className="text-xs text-black/40 mt-2 ml-1">
                    {SIGNAL_MODES.find(m => m.id === signalMode)?.description}
                  </p>
                </div>
              )}
            </div>

            {/* AI Working State - Scout branded streaming progress */}
            {isSearching && (
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-black/5 p-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] flex items-center justify-center">
                      <Wand2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] animate-ping opacity-30" />
                  </div>
                  <div className="flex-1">
                    <p 
                      className="text-lg font-semibold text-black"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {searchProgress.message || 'Scout is thinking...'}
                    </p>
                    <p className="text-xs text-black/40 mt-1">by EQ.app</p>
                    {/* Progress dots */}
                    <div className="flex items-center gap-1.5 mt-2">
                      {[1, 2, 3, 4].map((step) => (
                        <div 
                          key={step}
                          className={`w-2 h-2 rounded-full transition-all duration-500 ${
                            step <= (searchProgress.step || 1) 
                              ? 'bg-[#5a20ff]' 
                              : 'bg-black/10'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 text-center">
                {error}
              </div>
            )}

            {/* PillBuilder when typing (Signals/Jobs modes only — hidden for People mode) */}
            {!precisionSearch && showPillBuilder && query.trim().length > 3 && !isSearching && (
              <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-black/5 p-5 animate-in fade-in slide-in-from-bottom-2 duration-300">
                <PillBuilder
                  query={query}
                  mode={searchMode}
                  location={location}
                  onFiltersChange={(filters, outputFields) => {
                    setPillFilters(filters);
                    setPillOutputFields(outputFields);
                  }}
                  showOutputFields={true}
                  compact={true}
                />
                
                {/* Ranking Weight Controls */}
                <div className="mt-4 pt-4 border-t border-black/5">
                  <RankingWeightControls
                    weights={rankingWeights}
                    onChange={setRankingWeights}
                    variant="simple"
                    showPresets={false}
                  />
                </div>
              </div>
            )}

            {/* People/Company Search Panel — ContactOut parameters */}
            {/* Company mode: always visible (filters ARE the primary input) */}
            {/* People mode: visible after typing 4+ chars */}
            {precisionSearch && (searchMode === 'company' || query.trim().length > 3) && !isSearching && (
              <div className={`bg-white/80 backdrop-blur-xl rounded-2xl border p-5 animate-in fade-in slide-in-from-bottom-2 duration-300 ${
                searchMode === 'company' ? 'border-indigo-100/50' : 'border-emerald-100/50'
              }`}>
                <PrecisionPanel
                  query={query}
                  mode={searchMode}
                  location={location}
                  onRefinementsChange={setPrecisionRefinements}
                  isSearching={isSearching}
                />
              </div>
            )}
          </div>

          {/* Company Mode: Industry Quick-Picks when no query */}
          {searchMode === 'company' && !query.trim() && !isSearching && (
            <div className="mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="text-center mb-4">
                <p className="text-sm text-black/40" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Popular industries
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2 max-w-2xl mx-auto">
                {[
                  { label: 'Biotechnology', query: 'Biotech executives' },
                  { label: 'SaaS / Software', query: 'SaaS company leaders' },
                  { label: 'Financial Services', query: 'Financial services decision makers' },
                  { label: 'Healthcare', query: 'Healthcare company executives' },
                  { label: 'Medical Devices', query: 'Medical device company leaders' },
                  { label: 'Pharmaceuticals', query: 'Pharma company executives' },
                  { label: 'Manufacturing', query: 'Manufacturing company leaders' },
                  { label: 'E-commerce / Retail', query: 'E-commerce company executives' },
                  { label: 'Cybersecurity', query: 'Cybersecurity company leaders' },
                  { label: 'Energy / Renewables', query: 'Energy company executives' },
                  { label: 'Consulting', query: 'Consulting firm partners' },
                  { label: 'Insurance', query: 'Insurance company decision makers' },
                ].map(({ label, query: q }) => (
                  <button
                    key={label}
                    onClick={() => {
                      if (!isAuthenticated) {
                        requireAuth('search');
                        return;
                      }
                      setQuery(q);
                      setTimeout(() => inputRef.current?.focus(), 100);
                    }}
                    className="px-3.5 py-2 rounded-xl text-sm font-medium bg-white border border-black/[0.06] text-black/60 hover:border-indigo-300 hover:text-indigo-700 hover:bg-indigo-50/50 transition-all duration-200 shadow-sm hover:shadow"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Faststarts - One-click pre-populated searches when no query */}
          {searchMode !== 'company' && !query.trim() && (
            <div 
              className="mt-12 animate-in fade-in slide-in-from-bottom-4 duration-700" 
              style={{ animationDelay: '200ms' }}
            >
              <Faststarts 
                onSelect={(faststartQuery, mode) => {
                  // Gate faststarts for unauthenticated users
                  if (!isAuthenticated) {
                    requireAuth('search');
                    return;
                  }
                  setQuery(faststartQuery);
                  setSearchMode(mode);
                  // Auto-focus the input for editing
                  setTimeout(() => inputRef.current?.focus(), 100);
                }}
                hideFooter={true}
              />
            </div>
          )}

          {/* Keyboard Shortcut Hint - Subtle */}
          <div className="mt-12 text-center">
            <p className="text-[10px] text-black/20">
              Press <kbd className="px-1.5 py-0.5 bg-black/5 rounded text-black/30 font-mono text-[9px]">?</kbd> for shortcuts
            </p>
          </div>
        </div>
      </main>

      {/* Slide-out Agent Panel - LEFT side for consistency */}
      {showAgentGrid && (
        <>
          {/* Backdrop - Subtle blur */}
          <div 
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40"
            onClick={() => setShowAgentGrid(false)}
            aria-label="Close panel"
          />
          {/* Panel - Premium glass effect */}
          <div className="fixed top-0 left-0 h-full w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl z-50 flex flex-col animate-in slide-in-from-left duration-300">
            {/* Panel Header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-black/5 safe-area-top bg-gradient-to-r from-[#5a20ff]/5 to-[#52dcb3]/5">
              <button
                onClick={() => setShowAgentGrid(false)}
                className="flex items-center gap-1.5 text-[#5a20ff] text-sm font-medium hover:opacity-70 transition-opacity"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <h2 className="text-lg font-bold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                My Alerts
              </h2>
              <div className="w-16" /> {/* Spacer for centering */}
            </div>
            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto">
              <AgentGrid
                agents={feeds}
                isLoading={isLoadingFeeds}
                loadingAgentId={loadingAgentId}
                onSelectAgent={(agent) => {
                  setShowAgentGrid(false);
                  handleSelectAgentFromGrid(agent);
                }}
                onNewAgent={() => {
                  setShowAgentGrid(false);
                  setTimeout(() => inputRef.current?.focus(), 100);
                }}
                onDeleteAgent={handleDeleteAgentFromGrid}
                onShareAgent={handleShareAgentFromGrid}
                onRenameAgent={handleRenameAgentFromGrid}
                viewMode={agentGridViewMode}
                onViewModeChange={setAgentGridViewMode}
                hideHeader={false}
              />
            </div>
          </div>
        </>
      )}
      
      {/* Watch Website Modal */}
      <WatchWebsiteModal
        isOpen={showWatchWebsiteModal}
        onClose={() => setShowWatchWebsiteModal(false)}
        onSuccess={handleWatchWebsiteSuccess}
      />
      
      {/* Share Modal - Premium glass effect */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full max-w-md p-6 border border-black/5 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                Share Agent
              </h3>
              <button onClick={() => setShowShareModal(false)} className="p-2 hover:bg-black/5 rounded-full transition-colors">
                <X className="w-5 h-5 text-black/60" />
              </button>
            </div>
            <div className="flex items-center gap-2 p-3 bg-[#f8f8f6] rounded-xl">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 bg-transparent text-sm text-black/70 focus:outline-none"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareLink);
                }}
                className="px-3 py-1.5 bg-[#5a20ff] text-white text-sm rounded-lg hover:bg-[#5a20ff]/90"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-black/40 mt-3 text-center">
              Powered by EQ.app
            </p>
          </div>
        </div>
      )}

      {/* Integrations Modal */}
      {showIntegrations && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/5">
              <h3 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                Integrations
              </h3>
              <button onClick={() => setShowIntegrations(false)} className="p-1.5 hover:bg-black/5 rounded-lg">
                <X className="w-5 h-5 text-black/60" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* EQ Enrich */}
              <div className="p-4 border-2 border-[var(--eq-teal)]/20 rounded-xl bg-[var(--eq-teal)]/5">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[var(--eq-purple)] to-[var(--eq-teal)] flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                      EQ Enrich
                    </h4>
                    <p className="text-sm text-black/60">Enrich contacts with emails, phones, and company data</p>
                  </div>
                  <button 
                    onClick={() => setShowEnrichModal(true)}
                    className="px-4 py-2 bg-[var(--eq-teal)] text-white text-sm font-medium rounded-lg hover:bg-[var(--eq-teal)]/90 transition-colors"
                  >
                    Connect
                  </button>
                </div>
              </div>

              {/* Salesforce */}
              <div className="p-4 border border-black/10 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#00A1E0]/10 flex items-center justify-center">
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#00A1E0">
                      <path d="M10.006 5.415a4.195 4.195 0 013.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.16 2.37 5.16 5.295s-2.31 5.295-5.16 5.295c-.39 0-.765-.045-1.125-.12a3.72 3.72 0 01-3.27 1.95c-.57 0-1.11-.12-1.59-.345a4.425 4.425 0 01-3.93 2.4c-1.89 0-3.51-1.17-4.155-2.835a3.96 3.96 0 01-.93.105c-2.25 0-4.065-1.83-4.065-4.08s1.815-4.08 4.065-4.08c.255 0 .51.03.75.075a4.86 4.86 0 014.41-3.105h.005z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                      Salesforce
                    </h4>
                    <p className="text-sm text-black/60">Sync leads directly to your Salesforce CRM</p>
                  </div>
                  <span className="px-3 py-1.5 bg-black/5 text-black/50 text-xs font-medium rounded-lg">
                    Upon Request
                  </span>
                </div>
              </div>

              {/* HubSpot */}
              <div className="p-4 border border-black/10 rounded-xl">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#FF7A59]/10 flex items-center justify-center">
                    <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#FF7A59">
                      <path d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.984 2.21 2.21 0 00-4.42 0c0 .872.508 1.617 1.239 1.975v2.862a5.13 5.13 0 00-2.866 1.675l-7.41-5.77a2.617 2.617 0 00.084-.63 2.61 2.61 0 10-2.61 2.611c.46 0 .892-.12 1.267-.33l7.3 5.68a5.155 5.155 0 00-.103 1.008 5.18 5.18 0 005.179 5.179c.357 0 .703-.04 1.04-.108l2.639 2.639a1.875 1.875 0 102.65-2.65l-2.638-2.64a5.18 5.18 0 00-2.618-8.601z"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                      HubSpot
                    </h4>
                    <p className="text-sm text-black/60">Push leads to HubSpot contacts and deals</p>
                  </div>
                  <span className="px-3 py-1.5 bg-black/5 text-black/50 text-xs font-medium rounded-lg">
                    Upon Request
                  </span>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-black/5 bg-[#f8f8f6]">
              <p className="text-xs text-black/40 text-center">
                Need a custom integration? <a href="https://marcussawyerr.substack.com/" target="_blank" rel="noopener noreferrer" className="text-[var(--eq-purple-text)] hover:underline">Join the community</a>
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* New Leads Notification */}
      {newLeadsNotification && (
        <div className="fixed bottom-24 right-6 z-50 animate-in slide-in-from-bottom-4 duration-300">
          <div className="bg-white rounded-2xl shadow-2xl border border-[#52dcb3]/20 p-4 flex items-center gap-4 max-w-sm">
            <div className="w-12 h-12 rounded-xl bg-[var(--eq-teal)] flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
                {newLeadsNotification.count} new leads found
              </p>
              <p className="text-xs text-black/50 truncate">
                from {newLeadsNotification.feedName}
              </p>
            </div>
            <button
              onClick={() => {
                if (newLeadsNotification.feedId) {
                  navigate(`/app/feed/${newLeadsNotification.feedId}`);
                }
                setNewLeadsNotification(null);
              }}
              className="px-3 py-1.5 bg-[var(--eq-purple)] text-white text-xs font-semibold rounded-full hover:bg-[var(--eq-purple)]/90 transition-colors"
            >
              View
            </button>
            <button
              onClick={() => setNewLeadsNotification(null)}
              className="p-1 text-black/30 hover:text-black/60"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Sign Up Gate Modal - For landing page (unauthenticated users) */}
      <SignUpGateModal 
        isOpen={showGate} 
        onClose={closeGate} 
        feature={gateFeature}
      />
      </div>
      </DebugErrorBoundary>
    </SearchProvider>
  );
}

// =============================================================================
// Field Configuration Panel - Toggle fields for feed customization
// =============================================================================
function FieldConfigPanel({ searchType, fieldConfig, enabledFields, onToggleField, onToggleCategory }) {
  const categoryIcons = {
    core: Users,
    professional: GraduationCap,
    location: MapPin,
    company: Building2,
    social: Globe,
    metadata: FileText,
    size: TrendingUp,
    funding: DollarSign,
    contacts: Users,
    signals: Zap,
    job: Briefcase,
    source: Link2,
    news: Newspaper,
    agent: Cpu,
  };

  // Collapsible state for categories
  const [expandedCategories, setExpandedCategories] = React.useState({});
  
  const toggleExpanded = (key) => {
    setExpandedCategories(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="mt-2 p-3 bg-[#f8f8f6] rounded-lg border border-black/5">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
          {searchType === 'people' ? 'Profile Fields' : 
           searchType === 'company' ? 'Company Fields' : 'Job & News Fields'}
        </h4>
        <span className="text-xs text-[var(--eq-purple-text)] font-medium">
          {Object.values(enabledFields).filter(Boolean).length} selected
        </span>
      </div>

      {/* Categories - Compact accordion style */}
      <div className="space-y-1.5">
        {Object.entries(fieldConfig).map(([categoryKey, category]) => {
          const CategoryIcon = categoryIcons[categoryKey] || FileText;
          const enabledCount = category.fields.filter(f => enabledFields[f.id]).length;
          const allEnabled = enabledCount === category.fields.length;
          const someEnabled = enabledCount > 0 && !allEnabled;
          const isExpanded = expandedCategories[categoryKey];

          return (
            <div key={categoryKey} className="bg-white rounded-lg border border-black/5 overflow-hidden">
              {/* Category Header - Clickable to expand */}
              <button
                onClick={() => toggleExpanded(categoryKey)}
                className="w-full flex items-center justify-between px-2.5 py-2 hover:bg-black/[0.02] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <CategoryIcon className="w-3.5 h-3.5 text-[var(--eq-purple-text)]" />
                  <span className="text-xs font-medium text-black">{category.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                    allEnabled 
                      ? 'bg-[var(--eq-purple)] text-white' 
                      : someEnabled
                        ? 'bg-[var(--eq-purple)]/20 text-[var(--eq-purple-text)]'
                        : 'bg-black/5 text-black/40'
                  }`}>
                    {enabledCount}/{category.fields.length}
                  </span>
                  {isExpanded ? (
                    <ChevronUp className="w-3 h-3 text-black/40" />
                  ) : (
                    <ChevronDown className="w-3 h-3 text-black/40" />
                  )}
                </div>
              </button>

              {/* Fields Grid - Collapsible */}
              {isExpanded && (
                <div className="px-2.5 pb-2.5 pt-1 border-t border-black/5">
                  {/* Quick toggle all */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleCategory(category.fields, !allEnabled);
                    }}
                    className="text-[10px] text-[var(--eq-purple-text)] hover:underline mb-2"
                  >
                    {allEnabled ? 'Deselect all' : 'Select all'}
                  </button>
                  
                  <div className="grid grid-cols-2 gap-1">
                    {category.fields.map(field => (
                      <button
                        key={field.id}
                        onClick={() => onToggleField(field.id)}
                        className={`flex items-center gap-1.5 px-2 py-1 rounded text-[11px] text-left transition-all ${
                          enabledFields[field.id]
                            ? 'bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)]'
                            : 'bg-black/[0.02] text-black/50 hover:bg-black/5'
                        }`}
                      >
                        <div className={`w-3 h-3 rounded flex items-center justify-center flex-shrink-0 ${
                          enabledFields[field.id] ? 'bg-[var(--eq-purple)]' : 'bg-black/10'
                        }`}>
                          {enabledFields[field.id] && <Check className="w-2 h-2 text-white" />}
                        </div>
                        <span className="truncate">{field.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer hint */}
      <p className="text-[10px] text-black/40 text-center mt-3">
        Selected fields appear in results & exports
      </p>
    </div>
  );
}

// LeadCard is now imported from ../shared/LeadCard

// LeadDetailPanel is now imported from ../shared/LeadDetailPanel

// =============================================================================
// Kanban View Component - Drag leads between status columns
// Mobile responsive: stacks vertically on small screens
// =============================================================================
function KanbanView({ columns, statuses, selectedLead, onSelectLead, onStatusChange, customFields }) {
  const [activeColumn, setActiveColumn] = useState(statuses[0]?.id || 'inbox');
  
  return (
    <div className="flex-1 flex flex-col md:flex-row gap-3 p-4 overflow-hidden">
      {/* Mobile: Column tabs */}
      <div className="flex md:hidden gap-1 overflow-x-auto pb-2 flex-shrink-0">
        {statuses.map(status => {
          const StatusIcon = status.icon;
          const leads = columns[status.id] || [];
          const isActive = activeColumn === status.id;
          return (
            <button
              key={status.id}
              onClick={() => setActiveColumn(status.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                isActive 
                  ? 'bg-white shadow-md border-2' 
                  : 'bg-black/5 border-2 border-transparent'
              }`}
              style={{ borderColor: isActive ? status.color : 'transparent' }}
            >
              <StatusIcon className="w-3.5 h-3.5" style={{ color: status.color }} />
              <span>{status.label}</span>
              <span className="bg-black/10 px-1.5 py-0.5 rounded-full text-[10px]">{leads.length}</span>
            </button>
          );
        })}
      </div>

      {/* Desktop: All columns side by side */}
      <div className="hidden md:flex flex-1 gap-3 overflow-x-auto">
        {statuses.map(status => {
          const StatusIcon = status.icon;
          const leads = columns[status.id] || [];
          
          return (
            <div key={status.id} className="w-64 lg:w-72 flex-shrink-0 flex flex-col">
              {/* Column Header */}
              <div className="flex items-center gap-2 mb-3 px-2">
                <StatusIcon className="w-4 h-4" style={{ color: status.color }} />
                <span className="text-sm font-semibold text-black">{status.label}</span>
                <span className="text-xs text-black/40 bg-black/5 px-1.5 py-0.5 rounded-full">
                  {leads.length}
                </span>
              </div>
              
              {/* Column Content */}
              <div className="flex-1 bg-black/[0.02] rounded-xl p-2 space-y-2 overflow-y-auto min-h-[200px]">
                {renderKanbanCards(leads, status, selectedLead, onSelectLead, onStatusChange, statuses)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile: Single active column */}
      <div className="flex md:hidden flex-1 flex-col overflow-hidden">
        {statuses.filter(s => s.id === activeColumn).map(status => {
          const leads = columns[status.id] || [];
          return (
            <div key={status.id} className="flex-1 bg-black/[0.02] rounded-xl p-2 space-y-2 overflow-y-auto">
              {renderKanbanCards(leads, status, selectedLead, onSelectLead, onStatusChange, statuses)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Helper function to render kanban cards (used in both desktop and mobile)
function renderKanbanCards(leads, status, selectedLead, onSelectLead, onStatusChange, statuses) {
  if (leads.length === 0) {
    return (
      <div className="text-xs text-black/30 text-center py-8">
        No leads in {status.label}
      </div>
    );
  }
  
  return leads.map((lead, index) => {
    const data = lead.data || lead.lead_data || lead;
    const fullName = data.name || data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || '';
    const jobTitle = data.jobTitle || data.title || '';
    const companyName = data.companyName || data.company_name || '';
    const isSelected = selectedLead === lead;
    // Ensure unique key: combine ID with index to prevent duplicate key warnings
    const uniqueKey = `${lead.id || lead.personId || 'unknown'}-${index}`;
    
    return (
      <div
        key={uniqueKey}
        onClick={() => onSelectLead(lead)}
        className={`p-3 bg-white rounded-lg border cursor-pointer transition-all hover:shadow-md ${
          isSelected ? 'border-[var(--eq-purple)] shadow-md' : 'border-black/5'
        }`}
      >
        <div className="flex items-start gap-2">
          <div 
            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
            style={{ background: `linear-gradient(135deg, ${status.color}, #52dcb3)` }}
          >
            {fullName.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-black truncate">{fullName}</p>
            <p className="text-xs text-black/50 truncate">{jobTitle}</p>
            {companyName && (
              <p className="text-xs text-black/40 truncate">{companyName}</p>
            )}
          </div>
        </div>
        
        {/* Status change dropdown */}
        <div className="mt-2 pt-2 border-t border-black/5">
          <select
            value={status.id}
            onChange={(e) => {
              e.stopPropagation();
              onStatusChange(lead.id || lead.personId, e.target.value);
            }}
            onClick={(e) => e.stopPropagation()}
            className="w-full text-xs bg-black/5 rounded px-2 py-1 focus:outline-none"
          >
            {statuses.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>
    );
  });
}

// =============================================================================
// Custom Field Modal - Add SERP-based custom fields
// =============================================================================
function CustomFieldModal({ onClose, onAdd }) {
  const [selectedType, setSelectedType] = useState('news');
  const [label, setLabel] = useState('');
  const [queryTemplate, setQueryTemplate] = useState('');

  const handleAdd = () => {
    if (!label.trim()) return;
    
    onAdd({
      type: selectedType,
      label: label.trim(),
      queryTemplate: queryTemplate.trim() || CUSTOM_FIELD_TYPES.find(t => t.id === selectedType)?.placeholder || '',
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
            Add Custom Field
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-black/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-black/60">
            Create a custom field that fetches data from web search for each lead.
          </p>

          {/* Field Type Selection */}
          <div>
            <label className="text-xs font-medium text-black/60 uppercase tracking-wide mb-2 block">
              Field Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              {CUSTOM_FIELD_TYPES.map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      setSelectedType(type.id);
                      setQueryTemplate(type.placeholder);
                    }}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      selectedType === type.id
                        ? 'border-[var(--eq-purple)] bg-[var(--eq-purple)]/10'
                        : 'border-black/10 hover:border-[var(--eq-purple)]/30'
                    }`}
                  >
                    <Icon className={`w-5 h-5 mx-auto mb-1 ${selectedType === type.id ? 'text-[var(--eq-purple-text)]' : 'text-black/40'}`} />
                    <span className={`text-xs ${selectedType === type.id ? 'text-[var(--eq-purple-text)]' : 'text-black/60'}`}>
                      {type.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Field Label */}
          <div>
            <label className="text-xs font-medium text-black/60 uppercase tracking-wide mb-2 block">
              Field Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Recent Funding News"
              className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:border-[var(--eq-purple)]"
            />
          </div>

          {/* Query Template */}
          <div>
            <label className="text-xs font-medium text-black/60 uppercase tracking-wide mb-2 block">
              Search Query Template
            </label>
            <textarea
              value={queryTemplate}
              onChange={(e) => setQueryTemplate(e.target.value)}
              placeholder="Use {name}, {company}, {title}, {industry} as placeholders"
              rows={2}
              className="w-full px-3 py-2 border border-black/10 rounded-lg text-sm focus:outline-none focus:border-[var(--eq-purple)] resize-none"
            />
            <p className="text-xs text-black/40 mt-1">
              Variables: {'{name}'}, {'{company}'}, {'{title}'}, {'{industry}'}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-black/5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-black/60 hover:bg-black/5 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            disabled={!label.trim()}
            className="px-4 py-2 text-sm bg-[var(--eq-purple)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Add Field
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Share Modal - With "Powered by EQ.app" branding
// =============================================================================
function ShareModal({ shareLink, onClose, onCopy }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    onCopy?.();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[var(--eq-purple-text)]" />
            <h3 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
              Share This List
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-black/5 rounded-lg transition-colors">
            <X className="w-5 h-5 text-black/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-black/60">
            Anyone with this link can view this list. Your shared lists are branded with your identity.
          </p>

          {/* Link Preview */}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={shareLink}
              readOnly
              className="flex-1 px-3 py-2 bg-black/5 rounded-lg text-sm text-black/70 focus:outline-none"
            />
            <button
              onClick={handleCopy}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                copied 
                  ? 'bg-[var(--eq-teal)] text-white' 
                  : 'bg-[var(--eq-purple)] text-white hover:opacity-90'
              }`}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          {/* Powered by EQ.app - with REAL logo */}
          <div className="pt-4 border-t border-black/5 flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded-lg overflow-hidden border border-[var(--eq-purple)]/20 bg-white flex items-center justify-center">
              <img src="/favicon/favicon.svg" alt="EQ.app" className="w-5 h-5 object-contain" />
            </div>
            <span className="text-sm font-semibold bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] bg-clip-text text-transparent">
              Powered by EQ.app
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-black/5 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-black/60 hover:bg-black/5 rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Enrich Modal - Upload list and select fields to enrich
// =============================================================================
function EnrichModal({ onClose, onEnrich, progress }) {
  const [file, setFile] = useState(null);
  const [selectedFields, setSelectedFields] = useState({
    email: true,
    phone: true,
    company: true,
    title: true,
    location: true,
    linkedin: false,
    industry: false,
    revenue: false,
    employees: false,
  });
  const fileInputRef = useRef(null);

  const ENRICH_FIELDS = [
    { id: 'email', label: 'Email Address', icon: Mail },
    { id: 'phone', label: 'Phone Number', icon: Phone },
    { id: 'company', label: 'Company Info', icon: Building2 },
    { id: 'title', label: 'Job Title', icon: Briefcase },
    { id: 'location', label: 'Location', icon: MapPin },
    { id: 'linkedin', label: 'LinkedIn URL', icon: Linkedin },
    { id: 'industry', label: 'Industry', icon: Layers },
    { id: 'revenue', label: 'Company Revenue', icon: DollarSign },
    { id: 'employees', label: 'Employee Count', icon: Users },
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile && (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.json'))) {
      setFile(droppedFile);
    }
  };

  const handleSubmit = () => {
    if (!file) return;
    const fieldsToEnrich = Object.entries(selectedFields)
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);
    onEnrich(file, fieldsToEnrich);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-[var(--eq-purple-text)]" />
            <h3 className="text-lg font-semibold text-black" style={{ fontFamily: "'Playfair Display', serif" }}>
              Enrich Your List
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-black/5 rounded-lg transition-colors">
            <X className="w-5 h-5 text-black/60" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Progress indicator */}
          {progress && (
            <div className="p-4 rounded-xl bg-[var(--eq-purple)]/10 border border-[var(--eq-purple)]/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--eq-purple-text)]">
                  {progress.status === 'enriching' ? 'Enriching...' : progress.status === 'complete' ? 'Complete' : 'Error'}
                </span>
                <span className="text-xs text-[var(--eq-purple-text)]">
                  {progress.completed} / {progress.total}
                </span>
              </div>
              <div className="h-2 bg-[var(--eq-purple)]/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[var(--eq-purple)] transition-all duration-300"
                  style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                />
              </div>
              {progress.status === 'enriching' && (
                <p className="text-xs text-[var(--eq-purple-text)]/60 mt-2 flex items-center gap-1">
                  <Cpu className="w-3 h-3 animate-pulse" />
                  Agent is working in the background...
                </p>
              )}
            </div>
          )}

          {!progress && (
            <>
              <p className="text-sm text-black/60">
                Upload a CSV or JSON file from LinkedIn or any source. We'll enrich each contact with the fields you select.
              </p>

              {/* File Upload */}
              <div
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
                  file ? 'border-[var(--eq-purple)] bg-[var(--eq-purple)]/5' : 'border-black/10 hover:border-[var(--eq-purple)]/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.json"
                  onChange={handleFileChange}
                  className="hidden"
                />
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-[var(--eq-purple-text)]" />
                    <span className="text-sm font-medium text-[var(--eq-purple-text)]">{file.name}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="p-1 hover:bg-black/5 rounded"
                    >
                      <X className="w-4 h-4 text-black/40" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-black/30 mx-auto mb-2" />
                    <p className="text-sm text-black/60">Drop your file here or click to browse</p>
                    <p className="text-xs text-black/40 mt-1">Supports CSV and JSON</p>
                  </>
                )}
              </div>

              {/* Quick Upload Options */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-black/40">Quick:</span>
                <button className="px-2 py-1 bg-[#0077b5]/10 text-[#0077b5] rounded hover:bg-[#0077b5]/20 transition-colors">
                  LinkedIn Export
                </button>
                <button className="px-2 py-1 bg-black/5 text-black/60 rounded hover:bg-black/10 transition-colors">
                  Sample CSV
                </button>
              </div>

              {/* Field Selection */}
              <div>
                <label className="text-xs font-medium text-black/60 uppercase tracking-wide mb-2 block">
                  Fields to Enrich
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {ENRICH_FIELDS.map(field => {
                    const Icon = field.icon;
                    const isSelected = selectedFields[field.id];
                    return (
                      <button
                        key={field.id}
                        onClick={() => setSelectedFields(prev => ({ ...prev, [field.id]: !prev[field.id] }))}
                        className={`flex items-center gap-2 p-2 rounded-lg border text-xs transition-all ${
                          isSelected 
                            ? 'border-[var(--eq-purple)] bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)]' 
                            : 'border-black/10 text-black/50 hover:border-[var(--eq-purple)]/30'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        <span className="truncate">{field.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-black/5 flex justify-between items-center">
          <p className="text-xs text-black/40">
            {Object.values(selectedFields).filter(Boolean).length} fields selected
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-black/60 hover:bg-black/5 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!file || progress?.status === 'enriching'}
              className="px-4 py-2 text-sm bg-[var(--eq-purple)] text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
            >
              {progress?.status === 'enriching' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enriching...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Enrich List
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Similar Search Popup - Triggered when clicking a company
// =============================================================================
function SimilarSearchPopup({ companyName, position, onSearch, onClose }) {
  return (
    <div 
      className="absolute z-50 bg-white rounded-xl shadow-xl border border-black/10 p-2 w-56"
      style={{ top: position?.y || 0, left: position?.x || 0 }}
    >
      <div className="text-xs text-black/40 px-2 py-1 mb-1">Search for:</div>
      <button
        onClick={() => onSearch(companyName, 'people')}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-black/5 rounded-lg transition-colors"
      >
        <Users className="w-4 h-4 text-[var(--eq-purple-text)]" />
        <span>People at {companyName}</span>
      </button>
      <button
        onClick={() => onSearch(companyName, 'company')}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-black/5 rounded-lg transition-colors"
      >
        <Building2 className="w-4 h-4 text-[var(--eq-teal-text)]" />
        <span>Similar companies</span>
      </button>
      <button
        onClick={() => onSearch(companyName, 'jobs')}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-black/5 rounded-lg transition-colors"
      >
        <Briefcase className="w-4 h-4 text-orange-500" />
        <span>Open jobs at {companyName}</span>
      </button>
      <div className="border-t border-black/5 mt-1 pt-1">
        <button
          onClick={onClose}
          className="w-full px-3 py-1.5 text-xs text-black/40 hover:text-black/60 transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// Signal Preview Modal - Quick preview before opening article
// Company name and headline are clickable blue links to start new searches
// =============================================================================
function SignalPreviewModal({ signal, onClose, onLike, onArchive, onSearch }) {
  const data = signal.data || signal.lead_data || signal;
  
  const toText = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    if (typeof value === "object") {
      return value.name || value.title || value.label || value.source || fallback;
    }
    return fallback;
  };
  
  // Extract signal fields
  const headline = toText(data.headline || data.title || data.name);
  const snippet = toText(
    data.signalSnippet ||
      data.snippet ||
      data.body ||
      data.content ||
      data.highlight ||
      data.description ||
      data.summary
  );
  const signalType = toText(data.signalType || data.type, "News");
  const signalDate = toText(
    data.signalDate || data.date || data.publishedDate || data.published_at
  );
  const source = toText(data.signalSource || data.source || data.publisher);
  const sourceUrl =
    (typeof data.sourceUrl === "string" && data.sourceUrl) ||
    (typeof data.link === "string" && data.link) ||
    (typeof data.url === "string" && data.url) ||
    (typeof data.source?.link === "string" && data.source.link) ||
    (typeof data.source?.url === "string" && data.source.url) ||
    "";
  const sourceLogo =
    (typeof data.sourceLogo === "string" && data.sourceLogo) ||
    data.source?.icon ||
    data.source?.thumbnail ||
    data.thumbnail ||
    "";
  const companyName = toText(data.companyName || data.company || data.organization);
  const fundingAmount = toText(data.amount || data.fundingAmount || data.raised);
  
  // Check if new (within 24 hours)
  const isNew = React.useMemo(() => {
    if (!signalDate) return false;
    try {
      const articleDate = new Date(signalDate);
      const now = new Date();
      const hoursDiff = (now.getTime() - articleDate.getTime()) / (1000 * 60 * 60);
      return hoursDiff <= 24;
    } catch {
      return false;
    }
  }, [signalDate]);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[85vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isNew && (
              <span className="px-2 py-0.5 bg-red-500 text-white rounded text-xs font-bold">NEW</span>
            )}
            <span className="px-2 py-0.5 bg-[var(--eq-teal)]/20 text-[var(--eq-teal-text)] rounded text-xs font-semibold">{signalType}</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-black/40" />
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Company & Funding - Company is clickable to search */}
          {(companyName || fundingAmount) && (
            <div className="flex items-center gap-2 mb-3">
              {companyName && (
                <button
                  onClick={() => {
                    if (onSearch) {
                      onSearch(`Company: ${companyName}`, { companyName });
                      onClose();
                    }
                  }}
                  className="text-sm font-semibold text-[var(--eq-purple-text)] hover:underline cursor-pointer"
                  title={`Search for "${companyName}"`}
                >
                  {companyName} →
                </button>
              )}
              {fundingAmount && (
                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-bold">
                  {fundingAmount}
                </span>
              )}
            </div>
          )}
          
          {/* Headline - Clickable to start new search */}
          <button
            onClick={() => {
              if (onSearch && headline) {
                onSearch(headline, { query: headline });
                onClose();
              }
            }}
            className="text-xl font-bold text-[var(--eq-purple-text)] mb-3 leading-tight text-left hover:underline cursor-pointer w-full"
            style={{ fontFamily: "'Playfair Display', serif" }}
            title="Click to search this topic"
          >
            {headline}
          </button>
          
          {/* Snippet */}
          {snippet && (
            <p className="text-sm text-black/70 leading-relaxed mb-4">
              {snippet}
            </p>
          )}
          
          {/* Source */}
          <div className="flex items-center gap-2 text-xs text-black/40 mt-4 pt-4 border-t border-black/5">
            {sourceLogo && (
              <img 
                src={sourceLogo} 
                alt={source}
                className="w-4 h-4 rounded object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <span>{source}</span>
            {signalDate && <span>· {signalDate}</span>}
          </div>
        </div>
        
        {/* Actions */}
        <div className="px-5 py-4 border-t border-black/5 flex items-center gap-3">
          {/* Archive */}
          <button
            onClick={onArchive}
            className="flex items-center gap-2 px-4 py-2.5 bg-black/5 text-black/60 rounded-xl text-sm font-medium hover:bg-black/10 transition-colors"
          >
            <X className="w-4 h-4" />
            Skip
          </button>
          
          {/* Like */}
          <button
            onClick={onLike}
            className="flex items-center gap-2 px-4 py-2.5 bg-[var(--eq-purple)]/10 text-[var(--eq-purple-text)] rounded-xl text-sm font-medium hover:bg-[var(--eq-purple)]/20 transition-colors"
          >
            <Star className="w-4 h-4" />
            Save
          </button>
          
          {/* Open Article */}
          {sourceUrl && (
            <a
              href={sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[var(--eq-purple)] to-[var(--eq-teal)] text-white rounded-xl text-sm font-semibold hover:shadow-lg transition-all"
            >
              <ExternalLink className="w-4 h-4" />
              Read Article
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
